/* ============================================================================
   dressing.js — deep in-character set dressing for the AGENTSEE floor.

   The character models can't carry the identity, so the rooms do. Each export
   builds one seat's props at absolute world coordinates, on top of whatever the
   host has already furnished. Nothing here reads `three` directly: every helper
   arrives on `ctx` so there is exactly one THREE instance.

   ctx = { THREE, world, B, Cyl, M, prop, desk, chair, shelf, plant, papers,
           stack, mug, candle, pendant, WOODD, WOODL, PAPER, WALL,
           SCREENS, BLINKS, CANDLES }

   HOUSE RULES OBEYED HERE
   - Palette: dark + warm. No cyan anywhere. Exactly one coral on the floor and
     it is the door in main.js — nothing below goes near 0xff6f61. Reds come
     from the existing oxblood/rust family (0x8a2f2a, 0x66332c, 0xd98a4a).
   - Nothing floats. Only two walls on this plan are tall enough to hang from
     (x=-23 and z=-12.5, both 2.25m). Every partition is LOW 0.95 or STUB 0.55,
     so anything that would have been wall-hung is instead floor-standing (easel,
     A-frame, stand, plinth, stretcher) or dropped from the roof beams at y3.32
     on a visible cable via `drop()`.
   - Corridor door lanes stay clear: each room's door gap is centre-x ±0.85
     (Assange ±1.2), and actors walk it. Nothing is placed in that lane within
     ~2m of the corridor wall.
   - Shadows: castShadow only on objects big enough to read. Everything under
     ~0.25m uses the no-shadow builders `b0` / `cyl0`.

   COST — MEASURED, not estimated: this file adds 734 draw calls across 13 seats
   (~56/seat), plus 6 async prop() instances. Per-seat counts are in each block
   header. Materials are shared through `pal()` (24 module-level materials, all
   routed through the host's cached `M()` so they dedupe with main.js); 51 of the
   734 are InstancedMesh standing in for ~430 individual objects, and detail that
   would have needed dozens of little meshes is baked into a canvas texture on one
   plane instead (the report shelf, the seal, the mural, the blackboard).
   If that is too rich: the cheapest 120 calls to reclaim are Ben's fire surround,
   Shakespeare's Globe model and Wozniak's arcade cabinet — each is one
   self-contained block you can comment out without touching anything else.

   ANIMATION HOOKS added: SCREENS +7 · BLINKS +5 · CANDLES +8.
   ========================================================================= */

/* ---------- shared palette: built once, off the host's cached M() ---------- */
let P = null;
function pal(ctx){
  if(P) return P;
  const M = ctx.M, T = ctx.THREE;
  P = {
    STEEL : M(0x6d717a,.40,.62),   IRON  : M(0x3c3f46,.62,.35),
    BRASS : M(0x8a6f42,.36,.70),   GOLD  : M(0xb59a52,.40,.66),
    JET   : M(0x17171a,.72,.06),   CHAR  : M(0x2b2b30,.70,.14),
    SLATE : M(0x45454c,.70,.22),   ALU   : M(0xb1b4b8,.26,.86),
    ALUD  : M(0x8d9095,.34,.80),   CREAM : M(0xd2ccbd,.86),
    LINEN : M(0xb9b2a2,1.0),       BONE  : M(0xd8cfba,.66),
    OX    : M(0x8a2f2a,.70),       RUST  : M(0x66332c,.84),
    OCHRE : M(0xd9a05e,.62),       AMBER : M(0xd98a4a,.66),
    OLIVE : M(0x4a6a5e,.88),       MOSS  : M(0x4f5a4e,.90),
    NAVY  : M(0x1e2436,.92),       PERI  : M(0x8291c9,.55,.15),
    KRAFT : M(0x8a7a5c,.92),       MANILA: M(0xb8a97e,.94),
    FELT  : M(0x3a342c,.98),       PLY   : M(0xa88e6c,.88),
    GLASS : new T.MeshStandardMaterial({ color:0xa8aaa2, roughness:.10, transparent:true, opacity:.20, depthWrite:false }),
    SMOKE : new T.MeshBasicMaterial({ color:0xcfc8b4, transparent:true, opacity:.10, blending:T.AdditiveBlending, depthWrite:false, side:T.DoubleSide }),
    SHAFT : new T.MeshBasicMaterial({ color:0xffe2b0, transparent:true, opacity:.055, blending:T.AdditiveBlending, depthWrite:false, side:T.DoubleSide }),
  };
  return P;
}

/* ---------- tiny kit ---------- */
/* no-shadow box / cylinder — the default for anything under ~0.25m */
function b0(ctx,w,h,d,mat,x,y,z,ry){ return ctx.B(w,h,d,mat,x,y,z,ry||0, ctx.world, false); }
function cyl0(ctx,r,h,mat,x,y,z,seg){ const m = ctx.Cyl(r,h,mat,x,y,z,ctx.world,seg||10); m.castShadow=false; return m; }

/* one canvas → one texture. Keep these 256–512 except for two heroes. */
function tex(ctx,w,h,draw){
  const c = document.createElement('canvas'); c.width=w; c.height=h;
  draw(c.getContext('2d'), w, h);
  const t = new ctx.THREE.CanvasTexture(c); t.colorSpace = ctx.THREE.SRGBColorSpace; return t;
}
/* a printed face: one plane, one draw call, replaces a pile of little meshes */
function pic(ctx,t,x,y,z,ry,w,h,o){
  o = o||{}; const T = ctx.THREE;
  const mat = new T.MeshStandardMaterial({ map:t, roughness:o.rough!=null?o.rough:.72,
    side: o.both ? T.DoubleSide : T.FrontSide, transparent:!!o.alpha });
  if(o.em){ mat.emissive = new T.Color(0xffffff); mat.emissiveMap = t; mat.emissiveIntensity = o.em; }
  const m = new T.Mesh(new T.PlaneGeometry(w,h), mat);
  m.position.set(x,y,z); m.rotation.y = ry||0; if(o.rx) m.rotation.x = o.rx; if(o.rz) m.rotation.z = o.rz;
  ctx.world.add(m); if(o.em) ctx.SCREENS.push({ m:mat, base:o.em });
  return m;
}
/* a floor decal (splatter, pooled light, ink) */
function decal(ctx,geo,mat,x,z,y,rz){
  const m = new ctx.THREE.Mesh(geo, mat); m.rotation.x = -Math.PI/2; if(rz) m.rotation.z = rz;
  m.position.set(x, y==null?0.016:y, z); ctx.world.add(m); return m;
}
/* n copies, one draw call. place(i,d) poses the dummy and returns a colour. */
function inst(ctx,geo,mat,n,place,colored,parent){
  const T = ctx.THREE;
  const im = new T.InstancedMesh(geo, mat, n);
  const d = new T.Object3D(), c = new T.Color();
  for(let i=0;i<n;i++){
    d.position.set(0,0,0); d.rotation.set(0,0,0); d.scale.set(1,1,1);
    const col = place(i,d); d.updateMatrix(); im.setMatrixAt(i,d.matrix);
    if(colored) im.setColorAt(i, c.setHex(col));
  }
  im.instanceMatrix.needsUpdate = true; if(im.instanceColor) im.instanceColor.needsUpdate = true;
  im.castShadow = false; im.receiveShadow = true; (parent||ctx.world).add(im); return im;
}
/* the only legal way to hang something high in this building: a visible cable
   from the roof beams (y 3.34) down to the fixing. */
function drop(ctx,x,z,y){ const c = ctx.Cyl(0.007, 3.32-y, pal(ctx).CHAR, x, (3.32+y)/2, z, ctx.world, 5); c.castShadow=false; return c; }
/* a fresh emissive material — one per lamp so CANDLES/BLINKS can drive it alone */
function glowMat(ctx,hex,glow,i){ return new ctx.THREE.MeshStandardMaterial({ color:hex, emissive:glow, emissiveIntensity:i, roughness:.42 }); }
/* an A-frame easel: the floor-standing answer to every wall we don't have */
function easel(ctx,x,z,ry,h){
  const T = ctx.THREE, p = pal(ctx);
  const g = new T.Group(); g.position.set(x,0,z); g.rotation.y = ry; ctx.world.add(g);
  h = h || 1.6;
  const L = (dx,dz,tilt)=>{ const l = ctx.Cyl(0.024,h,p.KRAFT, dx,h/2,dz, g, 7); l.rotation.x = tilt; l.rotation.z = dx>0?-0.07:(dx<0?0.07:0); };
  L(-0.34,0.10,0.07); L(0.34,0.10,0.07); L(0,-0.28,-0.20);
  ctx.B(0.80,0.045,0.05, p.KRAFT, 0, h*0.36, 0.13, 0, g);          // the ledge the board sits on
  return g;
}

/* ============================================================================
   JOBS — room [12,-12.5 → 19,-2] · door lane x 14.6..16.4
   Story: the man who emptied the house, then put one lit thing back in it.
   29 draw calls · bbox x 12.36..17.81, z -11.19..-3.02
   ========================================================================= */
export function dressJobs(ctx){
  const T = ctx.THREE, p = pal(ctx), { B, Cyl, M, world } = ctx;

  /* the famous bare floor: a mat, a mattress, nothing else — the Woodside house */
  B(1.75,0.02,2.35, M(0x3a332a,.98), 13.4,0.026,-5.0, 0.06, world, false);
  const mat = B(1.00,0.17,1.94, p.CREAM, 13.38,0.10,-5.30, 0.04);            // mattress on the floor
  mat.receiveShadow = true;
  b0(ctx,0.98,0.05,0.62, p.LINEN, 13.38,0.205,-4.62, 0.04);                  // one folded blanket
  b0(ctx,0.44,0.09,0.30, p.CREAM, 13.30,0.215,-6.10, -0.22);                 // one pillow
  /* the two sandals he'd have left there */
  b0(ctx,0.11,0.03,0.27, p.KRAFT, 12.88,0.016,-4.32, 0.30);
  b0(ctx,0.11,0.03,0.27, p.KRAFT, 13.07,0.016,-4.38, 0.42);

  /* the Tiffany floor lamp — the one thing in the photograph besides him */
  {
    const lg = new T.Group(); lg.position.set(12.70,0,-3.55); world.add(lg);
    Cyl(0.16,0.035, p.BRASS, 0,0.018,0, lg, 14);                             // foot
    Cyl(0.022,1.34, p.BRASS, 0,0.69,0, lg, 8);                               // stem
    const leaded = tex(ctx,256,128,(g,w,h)=>{
      g.fillStyle='#1a1410'; g.fillRect(0,0,w,h);
      const pick = ['#d98a4a','#d9c26a','#8a5a34','#a86e3a','#c2543a'];
      for(let y=0;y<4;y++) for(let x=0;x<16;x++){
        g.fillStyle = pick[(x*3+y*5)%pick.length];
        g.globalAlpha = 0.72 + ((x*7+y)%5)*0.055;
        g.beginPath();
        g.moveTo(x*16, y*32); g.lineTo(x*16+16, y*32+4); g.lineTo(x*16+16, y*32+32); g.lineTo(x*16, y*32+28);
        g.fill();
      }
      g.globalAlpha=1; g.strokeStyle='#0d0b08'; g.lineWidth=2.5;
      for(let x=0;x<=16;x++){ g.beginPath(); g.moveTo(x*16,0); g.lineTo(x*16,h); g.stroke(); }
      for(let y=0;y<=4;y++){ g.beginPath(); g.moveTo(0,y*32); g.lineTo(w,y*32); g.stroke(); }
    });
    const shadeMat = new T.MeshStandardMaterial({ map:leaded, emissive:0xffffff, emissiveMap:leaded,
      emissiveIntensity:0.9, roughness:.55, side:T.DoubleSide });
    const shade = new T.Mesh(new T.CylinderGeometry(0.11,0.31,0.24,16,1,true), shadeMat);
    shade.position.set(0,1.44,0); lg.add(shade);
    ctx.CANDLES.push({ m:shadeMat, base:0.9, ph:1.4 });                      // it breathes like the candles do
    const pool = new T.Mesh(new T.CircleGeometry(0.28,16), glowMat(ctx,0xffd9a0,0xffc37a,1.1));
    pool.rotation.x = Math.PI/2; pool.position.set(0,1.33,0); lg.add(pool);
  }

  /* one apple on a plinth, lit from the roof — the whole company in one object */
  {
    B(0.28,1.00,0.28, M(0x232327,.72), 13.82,0.50,-3.20, 0, world);
    B(0.36,0.035,0.36, p.JET, 13.82,1.017,-3.20, 0, world);
    const ap = new T.Mesh(new T.SphereGeometry(0.062,16,13), M(0x8f2f26,.42));
    ap.scale.set(1,0.92,1); ap.position.set(13.82,1.095,-3.20); ap.castShadow = true; world.add(ap);
    cyl0(ctx,0.006,0.05, p.RUST, 13.82,1.17,-3.20, 5);                       // stalk
    b0(ctx,0.055,0.005,0.03, p.MOSS, 13.86,1.165,-3.19, 0.5);                // one leaf
    drop(ctx,13.82,-3.20,2.62);
    Cyl(0.055,0.09, p.CHAR, 13.82,2.60,-3.20, world, 10);
    const sp = new T.Mesh(new T.CircleGeometry(0.05,12), glowMat(ctx,0xffe2b0,0xffc37a,1.6));
    sp.rotation.x = Math.PI/2; sp.position.set(13.82,2.552,-3.20); world.add(sp);
  }

  /* the NeXT cube — the seven years nobody puts in the museum */
  {
    B(0.52,0.60,0.52, M(0x232327,.72), 17.55,0.30,-3.75, 0, world);
    const cube = B(0.30,0.30,0.30, M(0x1b1b1e,.55,.15), 17.55,0.752,-3.75, 0.10);
    cube.castShadow = true;
    const led = b0(ctx,0.022,0.022,0.008, glowMat(ctx,0x222222,0xd9a05e,1.3), 17.44,0.86,-3.60, 0.10);
    ctx.BLINKS.push({ m:led.material, ph:3.1 });
    b0(ctx,0.13,0.004,0.035, p.GOLD, 17.55,0.443,-3.49, 0.10);               // the plinth plaque
  }

  /* zen: books, a bowl and a tea set on the floor beside the cushion at (13.4,-10.2) */
  {
    const bg = new T.BoxGeometry(0.20,0.042,0.28);
    inst(ctx,bg,new T.MeshStandardMaterial({roughness:.9}),5,(i,d)=>{
      d.position.set(13.34,0.024+i*0.044,-11.02); d.rotation.y = i*0.19-0.2;
      return [0x4f5a4e,0x6b5566,0x5a5a46,0x77624a,0x4a4f5a][i];
    },true);
    const bowl = new T.Mesh(new T.SphereGeometry(0.055,14,8,0,6.3,0,1.05), p.BRASS);
    bowl.position.set(13.66,0.052,-11.02); bowl.rotation.x = Math.PI; world.add(bowl);
    cyl0(ctx,0.055,0.075, p.JET, 13.96,0.038,-10.28, 12);                    // iron teapot
    cyl0(ctx,0.022,0.02, p.JET, 13.96,0.086,-10.28, 8);
    cyl0(ctx,0.028,0.032, p.BONE, 14.14,0.016,-10.10, 10);                   // one cup
  }

  /* the calligraphy that made the Mac's typefaces — leaning on the floor */
  {
    const cal = tex(ctx,320,420,(g,w,h)=>{
      g.fillStyle='#cfc8b4'; g.fillRect(0,0,w,h);
      g.strokeStyle='#e0dac8'; g.lineWidth=1; for(let y=40;y<h;y+=64){ g.beginPath(); g.moveTo(24,y); g.lineTo(w-24,y); g.stroke(); }
      g.fillStyle='#1c1a17';
      const glyph=(x,y,s)=>{ g.save(); g.translate(x,y); g.scale(s,s);
        g.beginPath(); g.moveTo(-14,18); g.bezierCurveTo(-6,-14,10,-18,16,4); g.lineWidth=7; g.strokeStyle='#1c1a17'; g.stroke();
        g.beginPath(); g.moveTo(-2,-16); g.lineTo(4,20); g.lineWidth=5; g.stroke(); g.restore(); };
      for(let r=0;r<5;r++) for(let c2=0;c2<4;c2++) glyph(58+c2*70, 60+r*64, 0.8+((r+c2)%3)*0.12);
      g.font='italic 20px Georgia, serif'; g.fillStyle='#5a5348'; g.fillText('serif · sans · the space between', 40, h-24);
    });
    const g2 = new T.Group(); g2.position.set(12.38,0.50,-7.60); g2.rotation.y = Math.PI/2; g2.rotation.x = -0.12; world.add(g2);
    const fr = ctx.B(0.70,0.90,0.035, M(0x3a2f24,.78,.1), 0,0,0, 0, g2); fr.castShadow = true;
    const fc = new T.Mesh(new T.PlaneGeometry(0.61,0.80), new T.MeshStandardMaterial({ map:cal, roughness:.74 }));
    fc.position.z = 0.02; g2.add(fc);
  }
}

/* ============================================================================
   IVE — room [5,-12.5 → 12,-2] · door lane x 7.6..9.4
   Story: an empty room, three objects on one axis, and forty tries to get one.
   22 draw calls · bbox x 10.56..11.28, z -8.07..-3.18
   Deliberately the sparsest block in the file — the emptiness IS the character.
   ========================================================================= */
export function dressIve(ctx){
  const T = ctx.THREE, p = pal(ctx), { B, world, M } = ctx;
  const AX = 10.90;                                     // everything on one line, east of the door lane

  /* 1 · the unibody block: one piece of aluminium with a pocket milled out */
  B(0.46,0.86,0.38, M(0xdad5c9,.92), AX,0.43,-3.40, 0, world);               // white plinth
  B(0.52,0.03,0.44, M(0xe4e0d6,.88), AX,0.87,-3.40, 0, world);
  {
    const blk = B(0.30,0.055,0.21, p.ALU, AX,0.913,-3.40, 0.0); blk.castShadow = true;
    b0(ctx,0.22,0.03,0.14, p.ALUD, AX,0.928,-3.40, 0);                       // the milled pocket
    b0(ctx,0.30,0.004,0.21, M(0xc9ccd0,.18,.9), AX,0.941,-3.40, 0);          // the brushed top face
  }

  /* 2 · the rejects: six that weren't it, and the seventh that was */
  B(0.34,0.62,1.56, M(0xdad5c9,.92), AX,0.31,-5.40, 0, world);               // low white bench
  B(0.40,0.03,1.62, M(0xe4e0d6,.88), AX,0.635,-5.40, 0, world);
  {
    const g = new T.BoxGeometry(0.24,0.05,0.17);
    inst(ctx,g,M(0xece8de,.85),6,(i,d)=>{
      d.position.set(AX, 0.676, -6.05 + i*0.205);
      d.scale.set(0.80+i*0.055, 0.72+i*0.09, 0.86+i*0.04);                   // each one a little closer
    });
    const chosen = B(0.24,0.052,0.17, p.ALU, AX,0.677,-4.82, 0);             // number seven, in metal
    chosen.castShadow = true;
    /* one perfectly placed pencil, parallel to the bench edge, 40mm in */
    const pen = ctx.Cyl(0.0045,0.175, M(0xd9c26a,.7), AX+0.155,0.654,-5.40, world, 6);
    pen.rotation.x = Math.PI/2; pen.castShadow = false;
    const tip = ctx.Cyl(0.0045,0.018, p.JET, AX+0.155,0.654,-5.492, world, 6);
    tip.rotation.x = Math.PI/2; tip.castShadow = false;
  }

  /* 3 · the prototype nobody is allowed to see yet */
  {
    const g = new T.Group(); g.position.set(AX,0,-7.42); world.add(g);
    [[-0.55],[0.55]].forEach(q=>{                                            // two trestles
      const l1 = ctx.Cyl(0.022,0.72, p.ALUD, -0.13,0.36,q[0], g, 6); l1.rotation.z = 0.16;
      const l2 = ctx.Cyl(0.022,0.72, p.ALUD,  0.13,0.36,q[0], g, 6); l2.rotation.z = -0.16;
      ctx.B(0.36,0.03,0.05, p.ALUD, 0,0.70,q[0], 0, g);
    });
    ctx.B(0.52,0.035,1.30, p.ALUD, 0,0.735,0, 0, g);                         // the top
    const cloth = ctx.B(0.62,0.34,0.92, new T.MeshStandardMaterial({color:0xc6c0b0, roughness:1}), 0,0.92,0, 0.03, g);
    cloth.castShadow = true;
    ctx.B(0.66,0.05,0.98, new T.MeshStandardMaterial({color:0xbdb7a7, roughness:1}), 0,0.76,0, 0.03, g);  // the hem
    const corner = ctx.B(0.30,0.012,0.26, new T.MeshStandardMaterial({color:0xc6c0b0, roughness:1, side:T.DoubleSide}), 0.20,0.78,0.40, -0.3, g, false);
    corner.rotation.x = 0.55;                                                 // one corner lifted
    ctx.B(0.22,0.10,0.30, p.ALU, 0.02,0.80,0.10, 0, g, false);               // what shows underneath
  }
}

/* ============================================================================
   WOZNIAK — room [-3,-12.5 → 5,-2] · door lane x 0.1..1.9
   Story: the engineer never cleaned the bench, and the bench is the biography.
   70 draw calls · bbox x -3.01..4.74, z -8.37..-2.42
   ========================================================================= */
export function dressWozniak(ctx){
  const T = ctx.THREE, p = pal(ctx), { B, Cyl, M, world } = ctx;

  /* --- the HAM shack: the hobby that predates the company --- */
  {
    const g = new T.Group(); g.position.set(-2.25,0,-3.45); g.rotation.y = 0.22; world.add(g);
    ctx.B(1.40,0.06,0.66, ctx.WOODD, 0,0.72,0, 0, g);                        // bench
    [[-0.63,-0.26],[0.63,-0.26],[-0.63,0.26],[0.63,0.26]].forEach(q=>ctx.B(0.06,0.69,0.06, ctx.WOODD, q[0],0.345,q[1], 0, g));
    /* the transceiver, dial lit */
    ctx.B(0.46,0.22,0.30, M(0x6b6b60,.62,.2), -0.34,0.86,0, 0, g);
    const dial = tex(ctx,256,96,(gg,w,h)=>{
      gg.fillStyle='#12140f'; gg.fillRect(0,0,w,h);
      gg.strokeStyle='#8a9a5e'; gg.lineWidth=2;
      for(let i=0;i<26;i++){ const x=10+i*9; gg.beginPath(); gg.moveTo(x,h-8); gg.lineTo(x, h-8-(i%5===0?26:14)); gg.stroke(); }
      gg.fillStyle='#d9a05e'; gg.font='600 26px Menlo, monospace'; gg.fillText('14.070', 12, 34);
      gg.fillStyle='#8a9a5e'; gg.font='15px Menlo, monospace'; gg.fillText('MHz  USB  WA6BND', 118, 32);
      gg.fillStyle='#c2543a'; gg.fillRect(120, h-38, 3, 30);                 // the needle
    });
    const dm = new T.MeshStandardMaterial({ map:dial, emissive:0xffffff, emissiveMap:dial, emissiveIntensity:.55, roughness:.42 });
    const dp = ctx.B(0.40,0.15,0.012, dm, -0.34,0.87,0.153, 0, g, false);
    ctx.SCREENS.push({ m:dm, base:.55 });
    [[-0.50,0.78],[-0.18,0.78]].forEach(q=>{ const k = ctx.Cyl(0.024,0.022, p.CHAR, q[0],q[1],0.16, g, 10); k.rotation.x = Math.PI/2; k.castShadow=false; });
    /* the Morse key — brass on a black base */
    ctx.B(0.13,0.025,0.09, p.JET, 0.30,0.765,0.10, 0.2, g, false);
    const lev = ctx.B(0.10,0.010,0.016, p.BRASS, 0.30,0.792,0.10, 0.2, g, false); lev.rotation.z = -0.12;
    ctx.Cyl(0.017,0.012, p.JET, 0.345,0.80,0.105, g, 10).castShadow = false;
    /* the desk mic, on a coiled cord */
    ctx.Cyl(0.055,0.018, p.JET, 0.14,0.784,-0.16, g, 12).castShadow = false;
    const stem = ctx.Cyl(0.010,0.16, p.CHAR, 0.14,0.87,-0.16, g, 6); stem.rotation.x = -0.28; stem.castShadow=false;
    ctx.B(0.055,0.075,0.045, p.CHAR, 0.145,0.955,-0.135, 0, g, false);
    inst(ctx,new T.TorusGeometry(0.022,0.005,4,10), p.CHAR, 9,(i,d)=>{
      d.position.set(0.14+i*0.032, 0.755, -0.10+i*0.006); d.rotation.x = Math.PI/2; d.rotation.z = i*0.4;
    },false,g);
    /* the Rubik's cube he'd solve while a compile ran */
    const rub = tex(ctx,96,96,(gg)=>{ const c=['#c2543a','#d9c26a','#4a6a5e','#d98a4a','#cfc8b4','#8291c9'];
      gg.fillStyle='#101012'; gg.fillRect(0,0,96,96);
      for(let y=0;y<3;y++) for(let x=0;x<3;x++){ gg.fillStyle=c[(x*2+y*5)%6]; gg.fillRect(x*32+4,y*32+4,24,24); } });
    const rm = new T.Mesh(new T.BoxGeometry(0.075,0.075,0.075), new T.MeshStandardMaterial({map:rub, roughness:.62}));
    rm.position.set(0.56,0.795,-0.14); rm.rotation.y = 0.5; g.add(rm);
  }
  /* the antenna, guyed to a floor tripod — nothing hangs off these low walls */
  {
    const g = new T.Group(); g.position.set(-2.55,0,-2.62); world.add(g);
    inst(ctx,new T.BoxGeometry(0.035,0.44,0.035), p.STEEL, 3,(i,d)=>{
      const a = i/3*Math.PI*2; d.position.set(Math.cos(a)*0.15,0.21,Math.sin(a)*0.15); d.rotation.z = Math.cos(a)*0.3; d.rotation.x = -Math.sin(a)*0.3;
    },false,g);
    ctx.Cyl(0.05,0.14, p.CHAR, 0,0.47,0, g, 10);                             // loading coil
    inst(ctx,new T.TorusGeometry(0.052,0.007,4,12), M(0xa8783c,.4,.7), 8,(i,d)=>{ d.position.set(0,0.42+i*0.017,0); d.rotation.x = Math.PI/2; },false,g);
    const whip = ctx.Cyl(0.008,1.62, p.STEEL, 0,1.35,0, g, 6); whip.castShadow = false;
    cyl0(ctx,0.014,0.03, p.OX, -2.55,2.17,-2.62, 8);                         // the tip ball
  }

  /* --- the blue box: the first thing they ever sold, and it was a felony --- */
  {
    B(0.30,0.62,0.26, M(0x232327,.72), -0.95,0.31,-3.25, 0, world);
    B(0.36,0.03,0.32, p.JET, -0.95,0.637,-3.25, 0, world);
    const bb = B(0.21,0.055,0.135, p.PERI, -0.95,0.680,-3.25, 0.35); bb.castShadow = true;
    inst(ctx,new T.BoxGeometry(0.022,0.008,0.020), p.CREAM, 12,(i,d)=>{
      const c2 = i%3, r = (i/3)|0;
      d.position.set(-0.95 + Math.cos(0.35)*(-0.055+c2*0.037) - Math.sin(0.35)*(-0.042+r*0.028),
                     0.711,
                     -3.25 + Math.sin(0.35)*(-0.055+c2*0.037) + Math.cos(0.35)*(-0.042+r*0.028));
      d.rotation.y = 0.35;
    });
    inst(ctx,new T.CylinderGeometry(0.004,0.004,0.004,6), p.JET, 9,(i,d)=>{  // the speaker grille
      d.position.set(-0.95 + Math.cos(0.35)*(0.058) - Math.sin(0.35)*(-0.04+(i%3)*0.03),
                     0.709, -3.25 + Math.sin(0.35)*(0.058) + Math.cos(0.35)*(-0.04+((i/3)|0)*0.03));
    });
    b0(ctx,0.11,0.004,0.028, p.GOLD, -0.95,0.286,-3.11, 0);                  // the plinth plaque
  }

  /* --- the Apple I: a bare board screwed to a plank, on an easel --- */
  {
    const e = easel(ctx, 3.30, -3.40, -0.90, 1.55);
    ctx.B(0.72,0.50,0.035, p.PLY, 0,0.86,0.135, 0, e);                       // the plank it was always sold on
    const pcb = tex(ctx,320,224,(g,w,h)=>{
      g.fillStyle='#2f4a30'; g.fillRect(0,0,w,h);
      g.strokeStyle='#b7a44a'; g.lineWidth=2;
      for(let i=0;i<40;i++){ const y=8+((i*11)%(h-16)); g.beginPath(); g.moveTo(6,y); g.lineTo(6+((i*37)%(w-40)), y); g.lineTo(6+((i*37)%(w-40))+16, y+((i%3)-1)*14); g.stroke(); }
      g.fillStyle='#c9b76a'; for(let i=0;i<90;i++) g.fillRect(10+((i*29)%(w-20)), 10+((i*47)%(h-20)), 3, 3);
      g.fillStyle='#d8cfba'; g.font='11px Menlo, monospace'; g.fillText('APPLE COMPUTER 1  © 1976', 10, h-9);
    });
    const pf = new T.Mesh(new T.PlaneGeometry(0.60,0.42), new T.MeshStandardMaterial({ map:pcb, roughness:.62 }));
    pf.position.set(0,0.86,0.155); e.add(pf);
    inst(ctx,new T.BoxGeometry(0.052,0.011,0.017), p.JET, 22,(i,d)=>{        // the DIPs
      d.position.set(-0.24+(i%6)*0.093, 0.72+((i/6)|0)*0.088, 0.164); d.rotation.z = (i%4===3)?Math.PI/2:0;
    },false,e);
    ctx.B(0.16,0.04,0.008, p.GOLD, 0,0.605,0.158, 0, e, false);              // museum plaque
  }

  /* --- the arcade cabinet: Breakout, four days, fifty chips --- */
  {
    const g = new T.Group(); g.position.set(4.35,0,-5.65); g.rotation.y = -Math.PI/2; world.add(g);
    ctx.B(0.68,1.70,0.72, M(0x3a3128,.86), 0,0.85,0, 0, g);
    ctx.B(0.72,0.10,0.78, p.JET, 0,1.74,0, 0, g);                            // marquee cap
    const brk = tex(ctx,256,224,(gg,w,h)=>{
      gg.fillStyle='#0b0c0a'; gg.fillRect(0,0,w,h);
      const rows=['#c2543a','#d98a4a','#d9c26a','#4a6a5e'];
      for(let r=0;r<4;r++) for(let c2=0;c2<12;c2++){ gg.fillStyle=rows[r];
        if((r*12+c2)%13===5) continue; gg.fillRect(6+c2*20.5, 28+r*14, 17, 10); }
      gg.fillStyle='#cfc8b4'; gg.fillRect(96, h-20, 42, 6); gg.fillRect(150, 128, 6, 6);
      gg.fillStyle='#8a9a5e'; gg.font='600 15px Menlo, monospace'; gg.fillText('0480', 10, 18); gg.fillText('WOZ', w-52, 18);
    });
    const bm = new T.MeshStandardMaterial({ map:brk, emissive:0xffffff, emissiveMap:brk, emissiveIntensity:.62, roughness:.42 });
    ctx.B(0.54,0.44,0.012, bm, 0,1.24,0.335, 0, g, false);
    ctx.SCREENS.push({ m:bm, base:.62 });
    ctx.B(0.62,0.36,0.02, M(0x1c1c1f,.5), 0,1.24,0.31, 0, g, false);         // bezel
    const cp = ctx.B(0.66,0.05,0.30, M(0x2b2b30,.7), 0,0.98,0.30, 0, g); cp.rotation.x = -0.22;
    ctx.Cyl(0.045,0.045, p.OX, -0.14,1.02,0.30, g, 12).castShadow = false;   // the paddle knob
    ctx.Cyl(0.020,0.020, p.CREAM, 0.16,1.01,0.30, g, 10).castShadow = false;
    ctx.B(0.60,0.20,0.012, new T.MeshStandardMaterial({ color:0xd98a4a, emissive:0xd98a4a, emissiveIntensity:.5, roughness:.5 }), 0,1.62,0.34, 0, g, false);
  }

  /* --- solder station on the side bench: the smoke is still going up --- */
  {
    B(0.20,0.10,0.13, M(0x6b6b60,.62,.2), 4.35,0.825,-8.10, 0.3, world, false);  // the iron's stand
    const ir = Cyl(0.014,0.19, p.OX, 4.35,0.90,-8.10, world, 8); ir.rotation.z = 0.9; ir.rotation.y = 0.3; ir.castShadow = false;
    cyl0(ctx,0.004,0.06, p.STEEL, 4.44,0.955,-8.06, 6);
    const curve = new T.CatmullRomCurve3([
      new T.Vector3(4.44,0.99,-8.06), new T.Vector3(4.47,1.20,-8.02), new T.Vector3(4.40,1.42,-8.10),
      new T.Vector3(4.49,1.63,-8.04), new T.Vector3(4.42,1.83,-8.12), new T.Vector3(4.48,1.98,-8.06)]);
    const smoke = new T.Mesh(new T.TubeGeometry(curve, 26, 0.017, 6, false), p.SMOKE);
    world.add(smoke);
    cyl0(ctx,0.055,0.02, p.STEEL, 4.32,0.786,-8.32, 12);                     // solder spool
    inst(ctx,new T.TorusGeometry(0.048,0.004,4,12), M(0x9a9aa2,.4,.6), 6,(i,d)=>{
      d.position.set(4.32,0.796+i*0.005,-8.32); d.rotation.x = Math.PI/2;
    });
  }

  /* --- the dot-matrix, mid-job, paper on the floor --- */
  {
    const g = new T.Group(); g.position.set(-2.30,0,-6.15); g.rotation.y = 0.28; world.add(g);
    ctx.B(0.74,0.60,0.52, M(0x3f3a33,.88), 0,0.30,0, 0, g);                  // stand
    ctx.B(0.62,0.24,0.44, M(0xb9b2a2,.78), 0,0.72,0, 0, g);                  // printer
    ctx.B(0.52,0.05,0.10, M(0x2b2b30,.6), 0,0.86,-0.02, 0, g, false);        // paper slot
    const led = ctx.B(0.02,0.02,0.008, glowMat(ctx,0x222222,0xd9a05e,1.4), 0.24,0.79,0.223, 0, g, false);
    ctx.BLINKS.push({ m:led.material, ph:0.8 });
    inst(ctx,new T.BoxGeometry(0.40,0.008,0.13), ctx.PAPER, 14,(i,d)=>{      // fanfold, concertina to the floor
      const t2 = i/13;
      d.position.set(0, 0.84 - t2*0.80, 0.24 + t2*0.62);
      d.rotation.x = (i%2 ? 0.55 : -0.55) * (1-t2*0.55);
    },false,g);
    inst(ctx,new T.BoxGeometry(0.42,0.010,0.30), ctx.PAPER, 9,(i,d)=>{       // and the pile it made
      d.position.set(0.06, 0.006+i*0.011, 0.92+i*0.006); d.rotation.y = (i%3-1)*0.07;
    },false,g);
  }

  /* --- the diet: this is not a set-dressing joke, it is documented --- */
  {
    B(0.42,0.05,0.42, M(0xb08a5c,.94), 1.20,0.026,-6.90, 0.4, world, false); // pizza box, open
    const lid = B(0.42,0.012,0.42, M(0xa07c50,.94), 1.20,0.24,-7.22, 0.4, world, false); lid.rotation.x = -1.15;
    inst(ctx,new T.CylinderGeometry(0.10,0.10,0.012,3), M(0xc9a35c,.9), 3,(i,d)=>{
      d.position.set(1.14+((i%2)*0.13), 0.058, -6.86-(i*0.07)); d.rotation.y = i*2.1;
    });
    [[0.72,-6.30,0x4f5a4e],[0.86,-6.14,0x8a2f2a],[0.58,-6.06,0x3f4a3a]].forEach(q=>{  // bottles
      cyl0(ctx,0.033,0.20, M(q[2],.35,.25), q[0],0.10,q[1], 10);
      cyl0(ctx,0.014,0.05, M(q[2],.35,.25), q[0],0.22,q[1], 8);
    });
    inst(ctx,new T.SphereGeometry(0.038,7,6), M(0xcfc8b4,.9), 7,(i,d)=>{     // wrappers
      d.position.set(1.55+Math.sin(i*2.3)*0.42, 0.032, -6.55+Math.cos(i*1.7)*0.46);
      d.scale.set(1,0.55,1.1); d.rotation.y = i;
    });
    b0(ctx,0.16,0.22,0.06, M(0xd9c26a,.9), 1.72,0.11,-7.36, 0.7);            // a bag of chips, standing
  }

  /* --- the cable tangle every workshop grows --- */
  {
    [[0x2b2b30,2.60,-7.50,0.46],[0x4a4034,2.20,-7.90,0.34],[0x50505a,2.95,-7.90,0.30]].forEach(q=>{
      const pts = [];
      for(let i=0;i<=9;i++){ const a = i/9*Math.PI*2*1.35;
        pts.push(new T.Vector3(q[1]+Math.cos(a)*q[3]*(0.7+0.3*Math.sin(a*3)), 0.024+Math.sin(a*2)*0.012, q[2]+Math.sin(a)*q[3]*(0.7+0.3*Math.cos(a*2)))); }
      const cbl = new T.Mesh(new T.TubeGeometry(new T.CatmullRomCurve3(pts,true), 40, 0.011, 5, true), M(q[0],.85));
      cbl.receiveShadow = true; world.add(cbl);
    });
  }
}

/* ============================================================================
   SHAKESPEARE — room [-11,-12.5 → -3,-2] · door lane x -7.9..-6.1
   Story: fire for light, a wall of rolled drafts, and a toy theatre he keeps rebuilding.
   74 draw calls · bbox x -10.68..-3.18, z -11.77..-2.76
   ========================================================================= */
export function dressShakespeare(ctx){
  const T = ctx.THREE, p = pal(ctx), { B, Cyl, M, world } = ctx;

  /* --- the standing desk: he wrote upright, by candle --- */
  {
    const g = new T.Group(); g.position.set(-4.40,0,-3.30); g.rotation.y = -0.50; world.add(g);
    [[-0.32,-0.20,0.10,0.07],[0.32,-0.20,-0.10,0.07],[-0.32,0.20,0.10,-0.07],[0.32,0.20,-0.10,-0.07]].forEach(q=>{
      const l = ctx.Cyl(0.028,1.02, ctx.WOODD, q[0],0.51,q[1], g, 7); l.rotation.z = q[2]; l.rotation.x = q[3];
    });
    ctx.B(0.78,0.05,0.50, ctx.WOODD, 0,1.05,0, 0, g);
    const slope = ctx.B(0.74,0.035,0.46, ctx.WOODL, 0,1.11,-0.02, 0, g); slope.rotation.x = -0.30;
    ctx.B(0.76,0.035,0.05, ctx.WOODD, 0,1.045,0.225, 0, g, false);           // the pen lip
    ctx.B(0.30,0.004,0.40, ctx.PAPER, 0.06,1.135,-0.02, 0.06, g, false).rotation.x = -0.30;
    ctx.B(0.28,0.004,0.38, ctx.PAPER, -0.20,1.128,0.02, -0.14, g, false).rotation.x = -0.30;
    ctx.Cyl(0.036,0.07, M(0x1c1a20,.5,.2), 0.29,1.09,0.16, g, 10).castShadow=false;   // inkpot
    /* the three-branch candelabrum — real flames, on the CANDLES loop */
    ctx.Cyl(0.07,0.02, p.BRASS, -0.30,1.085,0.14, g, 12).castShadow=false;
    ctx.Cyl(0.014,0.20, p.BRASS, -0.30,1.18,0.14, g, 8).castShadow=false;
    ctx.B(0.26,0.014,0.014, p.BRASS, -0.30,1.28,0.14, 0, g, false);
    ctx.candle(-4.30, 1.29, -3.16, 0.020);
    ctx.candle(-4.47, 1.29, -3.26, 0.020);
    ctx.candle(-4.38, 1.15, -3.44, 0.024);
    /* the quill jar */
    ctx.Cyl(0.05,0.13, M(0x4a4034,.8), -0.14,1.115,0.15, g, 10).castShadow=false;
    inst(ctx,new T.BoxGeometry(0.008,0.30,0.022), p.BONE, 6,(i,d)=>{
      const a = i/6*Math.PI*2;
      d.position.set(-0.14+Math.cos(a)*0.02, 1.30, 0.15+Math.sin(a)*0.02);
      d.rotation.z = Math.cos(a)*0.24; d.rotation.x = Math.sin(a)*0.24;
    },false,g);
  }

  /* --- the sealing table: nothing leaves this room unsealed, and nothing leaves --- */
  {
    const g = new T.Group(); g.position.set(-10.25,0,-3.15); g.rotation.y = 0.35; world.add(g);
    ctx.B(0.72,0.05,0.56, ctx.WOODD, 0,0.74,0, 0, g);
    [[-0.31,-0.23],[0.31,-0.23],[-0.31,0.23],[0.31,0.23]].forEach(q=>ctx.B(0.055,0.72,0.055, ctx.WOODD, q[0],0.36,q[1], 0, g));
    inst(ctx,new T.CylinderGeometry(0.011,0.011,0.16,7), p.OX, 4,(i,d)=>{    // sticks of sealing wax
      d.position.set(-0.22+i*0.035, 0.775, -0.16); d.rotation.z = Math.PI/2; d.rotation.y = i*0.15;
    },false,g);
    ctx.Cyl(0.028,0.055, p.BRASS, 0.16,0.795,-0.12, g, 10).castShadow=false; // the seal matrix
    ctx.Cyl(0.012,0.09, ctx.WOODD, 0.16,0.865,-0.12, g, 6).castShadow=false;
    ctx.candle(-10.14, 0.775, -3.03, 0.022);                                 // the wax burner
    inst(ctx,new T.BoxGeometry(0.20,0.006,0.14), ctx.PAPER, 5,(i,d)=>{       // sealed letters, stacked
      d.position.set(0.02+((i%2)*0.03), 0.772+i*0.008, 0.16); d.rotation.y = (i-2)*0.12;
    },false,g);
    inst(ctx,new T.CylinderGeometry(0.016,0.016,0.004,10), p.OX, 5,(i,d)=>{  // the blobs of wax
      d.position.set(0.02+((i%2)*0.03), 0.777+i*0.008, 0.16);
    },false,g);
  }

  /* --- the scroll rack: every draft he refuses to throw away --- */
  {
    const g = new T.Group(); g.position.set(-3.42,0,-4.60); g.rotation.y = -Math.PI/2; world.add(g);
    ctx.B(1.60,0.05,0.42, ctx.WOODD, 0,0.06,0, 0, g);                        // plinth
    ctx.B(1.60,0.04,0.42, ctx.WOODD, 0,1.62,0, 0, g);                        // top
    ctx.B(0.05,1.60,0.42, ctx.WOODD, -0.78,0.82,0, 0, g);
    ctx.B(0.05,1.60,0.42, ctx.WOODD,  0.78,0.82,0, 0, g);
    ctx.B(1.60,0.05,0.42, ctx.WOODD, 0,0.85,0, 0, g);                        // mid rail
    inst(ctx,new T.BoxGeometry(1.54,0.028,0.40), ctx.WOODL, 6,(i,d)=>{       // pigeonhole shelves
      d.position.set(0, 0.20+i*0.24 + (i>2?0.06:0), 0);
    },false,g);
    inst(ctx,new T.BoxGeometry(0.028,0.22,0.40), ctx.WOODL, 14,(i,d)=>{      // dividers
      d.position.set(-0.68+ (i%7)*0.227, 0.32+((i/7)|0)*1.02, 0);
    },false,g);
    inst(ctx,new T.CylinderGeometry(0.036,0.036,0.36,9), M(0xcfc8b4,.94), 26,(i,d)=>{  // the scrolls
      const c2 = i%7, r = (i/7)|0;
      d.position.set(-0.68+c2*0.227 + ((i*3)%2)*0.055, 0.235+r*0.243 + (r>2?0.06:0), ((i*5)%3-1)*0.06);
      d.rotation.x = Math.PI/2; d.scale.set(0.78+((i*7)%4)*0.12, 1, 0.78+((i*7)%4)*0.12);
    },false,g);
    inst(ctx,new T.BoxGeometry(0.012,0.012,0.10), p.OX, 6,(i,d)=>{           // ribbon ends hanging out
      d.position.set(-0.62+i*0.24, 0.20+((i%3))*0.243, 0.24); d.rotation.x = 0.4;
    },false,g);
  }

  /* --- the Globe, in miniature, on a plinth: he keeps rebuilding the room he works for --- */
  {
    B(0.92,0.58,0.92, M(0x2f2921,.86), -9.00,0.29,-3.45, 0, world);
    B(1.00,0.035,1.00, ctx.WOODD, -9.00,0.597,-3.45, 0, world);
    const g = new T.Group(); g.position.set(-9.00,0.615,-3.45); g.rotation.y = 0.55; world.add(g);
    const wall = new T.Mesh(new T.CylinderGeometry(0.38,0.38,0.30,14,1,true, 0.85, 4.75), p.CREAM);
    wall.position.y = 0.15; wall.castShadow = true; g.add(wall);
    inst(ctx,new T.BoxGeometry(0.022,0.30,0.022), M(0x4a3c2c,.86), 14,(i,d)=>{   // the timber frame
      const a = 0.85 + (i/13)*4.75; d.position.set(Math.cos(a)*0.385, 0.15, Math.sin(a)*0.385); d.rotation.y = -a;
    },false,g);
    const roof = new T.Mesh(new T.TorusGeometry(0.375,0.055,6,22, 4.75), M(0xa07c4a,.95));
    roof.rotation.x = Math.PI/2; roof.rotation.z = -0.85; roof.position.y = 0.315; g.add(roof);
    const gal = new T.Mesh(new T.CylinderGeometry(0.32,0.32,0.012,14,1,true, 0.85, 4.75), M(0x77624a,.9));
    gal.position.y = 0.16; g.add(gal);
    ctx.B(0.32,0.055,0.24, M(0x77624a,.9), 0.04,0.028,0.13, -0.55, g);       // the thrust stage
    ctx.B(0.34,0.24,0.05, p.CREAM, 0.01,0.16,0.005, -0.55, g);               // tiring house
    ctx.Cyl(0.014,0.20, M(0x4a3c2c,.86), -0.09,0.155,0.19, g, 6).castShadow=false;
    ctx.Cyl(0.014,0.20, M(0x4a3c2c,.86),  0.15,0.155,0.13, g, 6).castShadow=false;
    ctx.Cyl(0.005,0.22, M(0x4a3c2c,.86), -0.19,0.44,-0.14, g, 5).castShadow=false;
    ctx.B(0.10,0.06,0.006, p.OX, -0.145,0.52,-0.14, 0, g, false);            // the flag that says: playing today
  }

  /* --- the hourglass on the writing table --- */
  {
    const g = new T.Group(); g.position.set(-5.90,0.775,-7.25); g.rotation.y = 0.4; world.add(g);
    ctx.Cyl(0.055,0.018, ctx.WOODD, 0,0.009,0, g, 12).castShadow=false;
    ctx.Cyl(0.055,0.018, ctx.WOODD, 0,0.191,0, g, 12).castShadow=false;
    inst(ctx,new T.BoxGeometry(0.010,0.19,0.010), ctx.WOODD, 3,(i,d)=>{
      const a = i/3*Math.PI*2; d.position.set(Math.cos(a)*0.046, 0.10, Math.sin(a)*0.046);
    },false,g);
    const b1 = new T.Mesh(new T.SphereGeometry(0.038,12,9), p.GLASS); b1.scale.set(1,0.95,1); b1.position.y = 0.062; g.add(b1);
    const b2 = new T.Mesh(new T.SphereGeometry(0.038,12,9), p.GLASS); b2.scale.set(1,0.95,1); b2.position.y = 0.138; g.add(b2);
    const sand = new T.Mesh(new T.SphereGeometry(0.030,10,7,0,6.3,1.3,1.85), M(0xc9a35c,.92));
    sand.position.y = 0.052; g.add(sand);
  }

  /* --- the property chest: a crown, a dagger, and the rest of the kingdom --- */
  {
    const g = new T.Group(); g.position.set(-9.60,0,-11.28); g.rotation.y = 0.18; world.add(g);
    ctx.B(0.92,0.44,0.54, M(0x4a3c2c,.88), 0,0.22,0, 0, g);
    const lid = new T.Mesh(new T.CylinderGeometry(0.27,0.27,0.90,12,1,false, 0, Math.PI), M(0x55432f,.88));
    lid.rotation.z = Math.PI/2; lid.rotation.y = Math.PI/2; lid.position.y = 0.44; lid.castShadow = true; g.add(lid);
    inst(ctx,new T.BoxGeometry(0.045,0.46,0.57), p.IRON, 2,(i,d)=>{ d.position.set(-0.26+i*0.52, 0.22, 0); },false,g);
    ctx.B(0.10,0.09,0.03, p.BRASS, 0,0.30,0.275, 0, g, false);               // the hasp
    /* the crown, set on the lid because it is a prop, not a symbol */
    const cr = new T.Mesh(new T.CylinderGeometry(0.075,0.082,0.055,10,1,true), p.GOLD);
    cr.position.set(-0.12,0.735,0.02); cr.castShadow = true; g.add(cr);
    inst(ctx,new T.ConeGeometry(0.016,0.05,4), p.GOLD, 6,(i,d)=>{
      const a = i/6*Math.PI*2; d.position.set(-0.12+Math.cos(a)*0.077, 0.788, 0.02+Math.sin(a)*0.077);
    },false,g);
    inst(ctx,new T.SphereGeometry(0.010,7,5), p.OX, 4,(i,d)=>{
      const a = i/4*Math.PI*2+0.4; d.position.set(-0.12+Math.cos(a)*0.079, 0.748, 0.02+Math.sin(a)*0.079);
    },false,g);
    /* the dagger — the one prop the whole canon needs */
    const bl = ctx.B(0.022,0.006,0.22, M(0xb9bcc2,.3,.8), 0.22,0.717,0.04, 0.5, g, false);
    ctx.B(0.075,0.012,0.016, p.BRASS, 0.245,0.717,0.16, 0.5, g, false);
    ctx.Cyl(0.014,0.10, M(0x3a2f24,.8), 0.265,0.717,0.215, g, 8).castShadow=false;
  }

  /* --- the ink he knocked over and never cleaned --- */
  {
    decal(ctx, new T.CircleGeometry(0.20,16),
      new T.MeshStandardMaterial({ color:0x191720, roughness:.55, transparent:true, opacity:.85, polygonOffset:true, polygonOffsetFactor:-1 }),
      -5.30, -8.65, 0.015, 0.6);
    decal(ctx, new T.CircleGeometry(0.07,10),
      new T.MeshStandardMaterial({ color:0x191720, roughness:.55, transparent:true, opacity:.7, polygonOffset:true, polygonOffsetFactor:-1 }),
      -5.02, -8.42, 0.015, 1.2);
    b0(ctx,0.20,0.028,0.16, M(0x8f887a,.98), -4.98,0.014,-8.92, 0.8);        // the rag that failed
    b0(ctx,0.16,0.024,0.13, M(0x7a746a,.98), -5.62,0.012,-8.34, -0.4);
  }
}

/* ============================================================================
   BUFFETT — room [-7,2 → 1,12.5] · door lane x -3.9..-2.1
   Story: sixty years of annual reports, a rolltop, a rotary phone, a card game.
   53 draw calls · bbox x -6.77..0.08, z 2.43..12.24
   (The room already holds the cash pallet, which runs x -1.5..2.4 at z 6.1..6.7 —
    everything here stays clear of it and of Janna's side of it.)
   ========================================================================= */
export function dressBuffett(ctx){
  const T = ctx.THREE, p = pal(ctx), { B, M, world } = ctx;

  /* --- the wall of annual reports, by year, floor-standing --- */
  {
    const g = new T.Group(); g.position.set(-2.90,0,12.06); g.rotation.y = Math.PI; world.add(g);
    ctx.B(3.44,1.94,0.04, M(0x2f2921,.9), 0,0.97,-0.16, 0, g);               // back
    ctx.B(0.05,1.94,0.34, ctx.WOODD, -1.71,0.97,0, 0, g);
    ctx.B(0.05,1.94,0.34, ctx.WOODD,  1.71,0.97,0, 0, g);
    [0.06,0.44,0.82,1.20,1.58,1.93].forEach(y=>ctx.B(3.44,0.045,0.34, ctx.WOODD, 0,y,0, 0, g));
    /* one 1024 canvas carries every spine — hundreds of reports, one draw call */
    const spines = tex(ctx,1024,512,(gg,w,h)=>{
      gg.fillStyle='#191712'; gg.fillRect(0,0,w,h);
      const base = ['#cfc8b4','#b8a97e','#a8a08a','#c2b898','#9d9682','#b5ac90'];
      for(let r=0;r<5;r++){
        const y0 = r*102, hh = 96;
        for(let i=0;i<34;i++){
          const x0 = 4+i*30, ww = 22+((i*7+r)%4)*2;
          gg.fillStyle = base[(i*3+r*2)%base.length];
          gg.fillRect(x0, y0+4+((i+r)%3), ww, hh-((i+r)%3)-4);
          gg.fillStyle = 'rgba(28,24,18,0.55)';
          gg.fillRect(x0, y0+16, ww, 5); gg.fillRect(x0, y0+hh-22, ww, 5);
          gg.save(); gg.translate(x0+ww/2, y0+hh/2); gg.rotate(-Math.PI/2);
          gg.fillStyle='#332c22'; gg.font='600 15px Georgia, serif'; gg.textAlign='center';
          gg.fillText(String(1965 + r*12 + Math.floor(i/3)), 0, 5);
          gg.restore();
        }
      }
    });
    const face = new T.Mesh(new T.PlaneGeometry(3.34,1.62), new T.MeshStandardMaterial({ map:spines, roughness:.86 }));
    face.position.set(0,0.98,0.055); g.add(face);
    inst(ctx,new T.BoxGeometry(0.24,0.032,0.30), M(0xb8a97e,.92), 7,(i,d)=>{ // the ones pulled out and left flat
      d.position.set(-1.30+i*0.44, 0.485+((i%2)*0.038), 0.03); d.rotation.y = (i%3-1)*0.06;
    },false,g);
    ctx.B(0.26,0.035,0.32, M(0xcfc8b4,.92), 1.20,1.245,0.06, 0.12, g, false);
    ctx.B(0.26,0.035,0.32, M(0xa8a08a,.92), 1.22,1.281,0.05, -0.08, g, false);
    /* the share certificate, propped on the top shelf — one share, never sold */
    const cert = tex(ctx,512,336,(gg,w,h)=>{
      gg.fillStyle='#e6dfc9'; gg.fillRect(0,0,w,h);
      gg.strokeStyle='#7a6a3e'; gg.lineWidth=2; gg.strokeRect(12,12,w-24,h-24);
      gg.lineWidth=1; gg.strokeStyle='rgba(122,106,62,0.5)';
      for(let i=0;i<26;i++){ gg.beginPath(); gg.arc(w/2, h/2, 30+i*8, 0, 6.3); gg.stroke(); }
      gg.fillStyle='#e6dfc9'; gg.fillRect(40,60,w-80,h-140);
      gg.strokeStyle='#7a6a3e'; gg.lineWidth=2; gg.strokeRect(40,60,w-80,h-140);
      gg.fillStyle='#332c22'; gg.textAlign='center';
      gg.font='700 34px Georgia, serif'; gg.fillText('BERKSHIRE HATHAWAY', w/2, 118);
      gg.font='18px Georgia, serif'; gg.fillText('I N C O R P O R A T E D', w/2, 148);
      gg.fillStyle='#7a6a3e'; gg.fillRect(w/2-90,166,180,2);
      gg.fillStyle='#332c22'; gg.font='italic 22px Georgia, serif';
      gg.fillText('ONE SHARE · CLASS A', w/2, 202);
      gg.font='14px Georgia, serif'; gg.fillStyle='#6a6154';
      gg.fillText('NOT FOR SALE — NOT AT ANY PRICE', w/2, 232);
      gg.fillText('transfer restricted · holder of record', w/2, h-46);
    });
    const cg = new T.Group(); cg.position.set(1.05,2.22,0.02); cg.rotation.x = 0.16; g.add(cg);
    ctx.B(0.62,0.44,0.03, M(0x3a2f24,.78,.1), 0,0,0, 0, cg);
    const cf = new T.Mesh(new T.PlaneGeometry(0.54,0.36), new T.MeshStandardMaterial({ map:cert, roughness:.7 }));
    cf.position.z = 0.02; cg.add(cf);
  }

  /* --- the rolltop: the desk of a man who does not need a second screen --- */
  {
    const g = new T.Group(); g.position.set(-0.35,0,3.40); g.rotation.y = -Math.PI/2; world.add(g);
    ctx.B(0.42,0.62,0.62, ctx.WOODD, -0.50,0.31,0, 0, g);                    // pedestals
    ctx.B(0.42,0.62,0.62, ctx.WOODD,  0.50,0.31,0, 0, g);
    inst(ctx,new T.BoxGeometry(0.30,0.012,0.016), p.BRASS, 6,(i,d)=>{        // drawer pulls
      d.position.set((i<3?-0.50:0.50), 0.17+(i%3)*0.18, 0.315);
    },false,g);
    ctx.B(1.46,0.055,0.66, ctx.WOODL, 0,0.65,0, 0, g);                       // writing top
    ctx.B(1.46,0.60,0.05, ctx.WOODD, 0,0.98,-0.31, 0, g);                    // the back
    ctx.B(1.46,0.05,0.32, ctx.WOODD, 0,1.30,-0.18, 0, g);
    inst(ctx,new T.BoxGeometry(1.36,0.026,0.20), ctx.WOODL, 3,(i,d)=>{ d.position.set(0,0.82+i*0.16,-0.22); },false,g);
    inst(ctx,new T.BoxGeometry(0.022,0.44,0.20), ctx.WOODL, 8,(i,d)=>{ d.position.set(-0.60+i*0.17,0.96,-0.22); },false,g);
    inst(ctx,new T.BoxGeometry(0.14,0.018,0.11), ctx.PAPER, 7,(i,d)=>{       // paper in the pigeonholes
      d.position.set(-0.52+i*0.17, 0.845+((i%3)*0.16), -0.16); d.rotation.x = 0.35;
    },false,g);
    /* the tambour hood, rolled halfway back */
    inst(ctx,new T.BoxGeometry(1.42,0.022,0.075), ctx.WOODD, 8,(i,d)=>{
      const a = 0.18 + i*0.165;
      d.position.set(0, 1.28 + Math.sin(a)*0.30, -0.48 + Math.cos(a)*0.30);
      d.rotation.x = -a;
    },false,g);
    /* the rotary phone — the only line in or out */
    ctx.B(0.20,0.075,0.16, p.JET, -0.44,0.725,0.06, 0.3, g);
    const dialTex = tex(ctx,128,128,(gg)=>{
      gg.fillStyle='#1b1b1e'; gg.fillRect(0,0,128,128);
      gg.fillStyle='#cfc8b4'; gg.beginPath(); gg.arc(64,64,54,0,6.3); gg.fill();
      gg.fillStyle='#1b1b1e'; gg.beginPath(); gg.arc(64,64,22,0,6.3); gg.fill();
      for(let i=0;i<10;i++){ const a=-2.4+i*0.55;
        gg.fillStyle='#1b1b1e'; gg.beginPath(); gg.arc(64+Math.cos(a)*38, 64+Math.sin(a)*38, 8, 0, 6.3); gg.fill();
        gg.fillStyle='#8f887a'; gg.font='700 11px Menlo, monospace'; gg.textAlign='center';
        gg.fillText(String((i+1)%10), 64+Math.cos(a)*38, 64+Math.sin(a)*38+4); }
    });
    const dm = new T.Mesh(new T.CircleGeometry(0.052,20), new T.MeshStandardMaterial({ map:dialTex, roughness:.5 }));
    dm.rotation.x = -Math.PI/2; dm.rotation.z = 0.3; dm.position.set(-0.44,0.764,0.06); g.add(dm);
    ctx.B(0.19,0.035,0.05, p.JET, -0.44,0.795,-0.03, 0.3, g, false);         // the handset
    ctx.B(0.05,0.045,0.05, p.JET, -0.515,0.782,-0.055, 0.3, g, false);
    ctx.B(0.05,0.045,0.05, p.JET, -0.365,0.782,-0.005, 0.3, g, false);
    inst(ctx,new T.TorusGeometry(0.020,0.005,4,10), p.JET, 10,(i,d)=>{       // the coiled cord
      d.position.set(-0.30+i*0.028, 0.672, -0.06+i*0.004); d.rotation.x = Math.PI/2; d.rotation.z = i*0.5;
    },false,g);
    ctx.B(0.24,0.020,0.32, ctx.PAPER, 0.34,0.688,0.06, -0.2, g, false);      // a single sheet of numbers
  }

  /* --- the bridge table: the game he plays more hours than he trades --- */
  {
    const g = new T.Group(); g.position.set(-5.10,0,3.55); world.add(g);
    ctx.B(0.92,0.045,0.92, M(0x2c3a30,.98), 0,0.715,0, 0, g);                // baize
    ctx.B(0.86,0.03,0.86, ctx.WOODD, 0,0.688,0, 0, g);
    [[-0.40,-0.40],[0.40,-0.40],[-0.40,0.40],[0.40,0.40]].forEach(q=>ctx.B(0.045,0.68,0.045, ctx.WOODD, q[0],0.34,q[1], 0, g));
    inst(ctx,new T.BoxGeometry(0.058,0.003,0.086), M(0xe4e0d2,.7), 26,(i,d)=>{   // four hands plus the trick
      const hand = (i/6)|0, k = i%6;
      if(hand>3){ d.position.set(((i%4)-1.5)*0.07, 0.742, ((i%3)-1)*0.06); d.rotation.y = i; return; }
      const ang = [0,Math.PI,Math.PI/2,-Math.PI/2][hand];
      const rx = Math.sin(ang)*0.31, rz = Math.cos(ang)*0.31;
      d.position.set(rx + Math.cos(ang)*(k-2.5)*0.036, 0.742 + k*0.0016, rz - Math.sin(ang)*(k-2.5)*0.036);
      d.rotation.y = ang + (k-2.5)*0.09;
    },false,g);
    ctx.B(0.13,0.028,0.09, M(0x8f887a,.9), 0.30,0.742,-0.24, 0.4, g, false); // the score pad
    ctx.Cyl(0.008,0.09, p.CHAR, 0.30,0.78,-0.20, g, 6).castShadow = false;
    /* See's Candies — the business he bought and never stopped eating */
    ctx.B(0.20,0.055,0.14, M(0x6b5566,.72), -0.26,0.752,0.24, 0.25, g, false);
    ctx.B(0.185,0.012,0.125, M(0xcfc8b4,.8), -0.26,0.784,0.24, 0.25, g, false);
    ctx.chair(-5.10,2.83, 0); ctx.chair(-5.10,4.27, Math.PI);
    ctx.chair(-5.82,3.55, Math.PI/2); ctx.chair(-4.38,3.55, -Math.PI/2);
  }

  /* --- the stock ticker under glass, tape spilling to the floor --- */
  {
    const g = new T.Group(); g.position.set(-6.50,0,2.75); g.rotation.y = 0.5; world.add(g);
    ctx.Cyl(0.20,0.06, p.JET, 0,0.03,0, g, 14);
    ctx.Cyl(0.055,0.86, p.JET, 0,0.46,0, g, 10);
    ctx.Cyl(0.19,0.05, ctx.WOODD, 0,0.915,0, g, 16);
    ctx.Cyl(0.075,0.09, p.BRASS, 0,0.985,0, g, 12);                          // the mechanism
    ctx.Cyl(0.052,0.03, p.BRASS, 0,1.05,0, g, 12).castShadow = false;
    const dome = new T.Mesh(new T.SphereGeometry(0.145,16,10,0,6.3,0,1.57), p.GLASS);
    dome.position.y = 0.94; g.add(dome);
    inst(ctx,new T.BoxGeometry(0.026,0.002,0.09), M(0xe4e0d2,.85), 22,(i,d)=>{   // the tape, running out
      const t2 = i/21;
      d.position.set(0.14 + t2*0.30, 0.93 - t2*t2*0.90, 0.02 + Math.sin(t2*7)*0.09);
      d.rotation.z = -t2*1.5; d.rotation.y = Math.sin(t2*5)*0.4;
    },false,g);
    inst(ctx,new T.TorusGeometry(0.10,0.012,4,14), M(0xe4e0d2,.85), 4,(i,d)=>{   // the coil it made
      d.position.set(0.46, 0.014+i*0.007, 0.16+i*0.03); d.rotation.x = Math.PI/2; d.rotation.z = i*0.9;
      d.scale.set(1-i*0.12,1-i*0.12,1);
    },false,g);
  }

  /* --- the morning papers, already read, kept anyway --- */
  inst(ctx,new T.BoxGeometry(0.32,0.014,0.42), M(0xb0a894,.95), 13,(i,d)=>{
    d.position.set(-6.30+((i%2)*0.02), 0.008+i*0.015, 5.90+((i%3)*0.015)); d.rotation.y = (i%5-2)*0.05;
  });
  b0(ctx,0.34,0.012,0.44, M(0xc2b89e,.95), -6.28,0.212,5.92, 0.10);
}

/* ============================================================================
   WRENCH — room [-15,2 → -7,12.5] · door lane x -11.9..-10.1
   Story: a real court needs a public gallery, a wig, and boxes of discovery.
   61 draw calls · bbox x -14.77..-7.26, z 2.43..12.12
   ========================================================================= */
export function dressWrench(ctx){
  const T = ctx.THREE, p = pal(ctx), { M, world } = ctx;

  /* --- the public gallery: the part of a court that makes it a court --- */
  [8.70, 9.75, 10.80].forEach((z,row)=>{
    const g = new T.Group(); g.position.set(-10.90,0,z); world.add(g);
    ctx.B(5.00,0.07,0.42, ctx.WOODD, 0,0.44,0, 0, g);                        // seat
    ctx.B(5.00,0.52,0.06, M(0x332b21,.86), 0,0.70,-0.20, 0, g);              // back
    ctx.B(0.09,0.46,0.44, ctx.WOODD, -2.50,0.22,0, 0, g);                    // cheeks
    ctx.B(0.09,0.46,0.44, ctx.WOODD,  2.50,0.22,0, 0, g);
    if(row===0) ctx.B(4.90,0.035,0.10, ctx.WOODL, 0,0.86,-0.235, 0, g, false);  // the front row gets a rail
  });
  /* someone left a coat and a folded order of business on the back row */
  b0(ctx,0.44,0.06,0.30, M(0x2b2b30,.95), -9.30,0.48,10.72, 0.3);
  b0(ctx,0.20,0.012,0.28, ctx.PAPER, -12.10,0.478,10.78, -0.2);

  /* --- the wig and the silk: nothing else says barrister this fast --- */
  {
    const g = new T.Group(); g.position.set(-7.90,0,9.30); g.rotation.y = 0.4; world.add(g);
    ctx.Cyl(0.17,0.035, ctx.WOODD, 0,0.017,0, g, 14);                        // stand
    ctx.Cyl(0.030,1.28, ctx.WOODD, 0,0.66,0, g, 8);
    ctx.Cyl(0.10,0.14, ctx.WOODD, 0,1.36,0, g, 12);                          // the block
    const w = new T.Mesh(new T.SphereGeometry(0.115,16,11,0,6.3,0,1.9), M(0xe0dbcc,.94));
    w.position.set(0,1.44,0); w.scale.set(1,0.92,1.05); w.castShadow = true; g.add(w);
    inst(ctx,new T.TorusGeometry(0.030,0.014,5,10), M(0xe0dbcc,.94), 10,(i,d)=>{  // the rolled tiers
      const side = i<5?-1:1, k = i%5;
      d.position.set(side*0.105, 1.40-k*0.042, -0.02+((k%2)*0.03)); d.rotation.y = Math.PI/2;
    },false,g);
    inst(ctx,new T.BoxGeometry(0.05,0.16,0.022), M(0xe0dbcc,.94), 2,(i,d)=>{ // the two tails down the back
      d.position.set(-0.04+i*0.08, 1.30, -0.10); d.rotation.x = 0.2;
    },false,g);
    /* the robe on the same stand, and the bands under it */
    ctx.B(0.44,0.86,0.10, M(0x141416,.98), 0,0.86,-0.09, 0, g);
    ctx.B(0.16,0.62,0.09, M(0x141416,.98), -0.27,0.92,-0.06, 0.22, g);
    ctx.B(0.16,0.62,0.09, M(0x141416,.98),  0.27,0.92,-0.06, -0.22, g);
    ctx.B(0.07,0.11,0.012, M(0xe4e0d2,.9), 0,1.24,-0.03, 0, g, false);       // the bands
  }

  /* --- a second run of law reports, floor-standing on the east side --- */
  ctx.shelf(-7.35, 11.30, -Math.PI/2, 2.2);

  /* --- the exhibit table: everything tagged, nothing filed --- */
  {
    const g = new T.Group(); g.position.set(-14.10,0,9.20); g.rotation.y = 0.30; world.add(g);
    ctx.B(0.90,0.05,1.60, ctx.WOODD, 0,0.76,0, 0, g);
    [[-0.38,-0.72],[0.38,-0.72],[-0.38,0.72],[0.38,0.72]].forEach(q=>ctx.B(0.06,0.74,0.06, ctx.WOODD, q[0],0.37,q[1], 0, g));
    ctx.B(0.09,0.045,0.16, p.CHAR, -0.16,0.808,-0.58, 0.2, g, false);        // a phone
    ctx.B(0.26,0.035,0.05, M(0x9a9aa2,.35,.7), 0.14,0.803,-0.20, -0.4, g, false);  // something with an edge
    ctx.B(0.22,0.09,0.16, M(0x8f887a,.9), -0.14,0.830,0.16, 0.1, g, false);  // a bagged item
    ctx.B(0.24,0.06,0.32, M(0xb8a97e,.94), 0.16,0.815,0.62, -0.15, g, false);// a ledger
    ctx.B(0.10,0.018,0.14, p.JET, -0.22,0.794,0.66, 0.3, g, false);          // a drive
    inst(ctx,new T.BoxGeometry(0.05,0.002,0.035), M(0xd9c26a,.9), 5,(i,d)=>{ // the numbered tags
      d.position.set([-0.16,0.14,-0.14,0.16,-0.22][i]+0.06, 0.792, [-0.58,-0.20,0.16,0.62,0.66][i]+0.10);
      d.rotation.y = i*0.6;
    },false,g);
  }

  /* --- the panelled screens either side of the door, where the arms hang --- */
  {
    const crest = tex(ctx,320,384,(gg,w,h)=>{
      gg.fillStyle='#241d15'; gg.fillRect(0,0,w,h);
      gg.strokeStyle='#b59a52'; gg.lineWidth=3; gg.strokeRect(16,16,w-32,h-32);
      gg.fillStyle='#8a2f2a';                                                 // the shield
      gg.beginPath(); gg.moveTo(160,90); gg.lineTo(238,90); gg.lineTo(238,214);
      gg.quadraticCurveTo(238,282,160,312); gg.quadraticCurveTo(82,282,82,214);
      gg.lineTo(82,90); gg.closePath(); gg.fill();
      gg.strokeStyle='#b59a52'; gg.lineWidth=4; gg.stroke();
      gg.fillStyle='#d9c26a';
      for(let i=0;i<3;i++){ gg.beginPath(); gg.arc(160, 130+i*54, 15, 0, 6.3); gg.fill(); }
      gg.fillStyle='#b59a52'; gg.fillRect(112,58,96,20);                      // the crown bar
      for(let i=0;i<4;i++){ gg.beginPath(); gg.moveTo(114+i*26,58); gg.lineTo(126+i*26,34); gg.lineTo(138+i*26,58); gg.fill(); }
      gg.fillStyle='#cfc8b4'; gg.textAlign='center'; gg.font='600 19px Georgia, serif';
      gg.fillText('PUT IT IN WRITING', 160, h-38);
    });
    const board = tex(ctx,320,384,(gg,w,h)=>{
      gg.fillStyle='#241d15'; gg.fillRect(0,0,w,h);
      gg.strokeStyle='#b59a52'; gg.lineWidth=3; gg.strokeRect(16,16,w-32,h-32);
      gg.fillStyle='#cfc8b4'; gg.textAlign='center'; gg.font='600 22px Georgia, serif';
      gg.fillText('MATTERS DRAFTED', 160, 62);
      gg.fillStyle='#b59a52'; gg.fillRect(70,76,180,2);
      gg.font='17px Georgia, serif'; gg.fillStyle='#a89f8a'; gg.textAlign='left';
      ['LEASE — FRONT','INSURANCE — MOTOR','SUPPLY — SCHEDULE 2','EMPLOYMENT — 4','DISPUTE — HELD','TENURE — CDC'].forEach((t,i)=>gg.fillText(t, 44, 118+i*38));
      gg.textAlign='center'; gg.font='italic 16px Georgia, serif'; gg.fillStyle='#6d675c';
      gg.fillText('FILED: none', 160, h-40);
    });
    [[-13.10, crest],[-8.90, board]].forEach(q=>{
      const g = new T.Group(); g.position.set(q[0],0,2.52); world.add(g);
      ctx.B(1.30,2.10,0.10, M(0x332b21,.88), 0,1.05,0, 0, g);
      ctx.B(1.42,0.07,0.18, ctx.WOODL, 0,2.13,0, 0, g, false);               // the cornice
      ctx.B(0.34,0.05,0.30, ctx.WOODD, -0.42,0.025,0.06, 0, g, false);       // visible feet
      ctx.B(0.34,0.05,0.30, ctx.WOODD,  0.42,0.025,0.06, 0, g, false);
      const f = new T.Mesh(new T.PlaneGeometry(0.92,1.10), new T.MeshStandardMaterial({ map:q[1], roughness:.74 }));
      f.position.set(0,1.30,0.053); g.add(f);
    });
  }

  /* --- pleadings, tied in tape, on both counsel tables --- */
  [[-12.95,5.70],[-9.75,5.70]].forEach((q,k)=>{
    const n = k?7:11;
    inst(ctx,new T.BoxGeometry(0.24,0.011,0.33), ctx.PAPER, n,(i,d)=>{
      d.position.set(q[0], 0.782+i*0.012, q[1]); d.rotation.y = (i%4-1.5)*0.045;
    });
    b0(ctx,0.26,0.004,0.016, M(0xb98a7c,.9), q[0],0.782+n*0.012,q[1], 0);    // the tape, crossed
    b0(ctx,0.016,0.004,0.35, M(0xb98a7c,.9), q[0],0.782+n*0.012,q[1], 0);
  });

  /* --- the book on the witness box, and the stenotype at the bar --- */
  b0(ctx,0.16,0.055,0.22, M(0x2f2018,.8), -8.58,0.875,3.42, 0.25);
  b0(ctx,0.015,0.004,0.10, p.GOLD, -8.58,0.904,3.40, 0.25);
  b0(ctx,0.055,0.004,0.015, p.GOLD, -8.58,0.904,3.42, 0.25);
  {
    const g = new T.Group(); g.position.set(-12.40,0,7.05); g.rotation.y = -0.25; world.add(g);
    inst(ctx,new T.BoxGeometry(0.030,0.72,0.030), p.CHAR, 3,(i,d)=>{
      const a = i/3*Math.PI*2; d.position.set(Math.cos(a)*0.14,0.36,Math.sin(a)*0.14);
      d.rotation.z = -Math.cos(a)*0.22; d.rotation.x = Math.sin(a)*0.22;
    },false,g);
    ctx.B(0.34,0.09,0.26, p.SLATE, 0,0.76,0, 0, g);
    inst(ctx,new T.CylinderGeometry(0.014,0.014,0.012,8), M(0xd8cfba,.7), 12,(i,d)=>{
      d.position.set(-0.11+(i%6)*0.044, 0.812, -0.04+((i/6)|0)*0.055);
    },false,g);
    inst(ctx,new T.BoxGeometry(0.05,0.002,0.09), ctx.PAPER, 8,(i,d)=>{       // the transcript coming off the top
      const t2 = i/7; d.position.set(0.02, 0.81 - t2*0.70, 0.16 + t2*0.34); d.rotation.x = (i%2?0.6:-0.6)*(1-t2*0.5);
    },false,g);
  }

  /* --- discovery: five boxes, lids labelled, nothing lodged --- */
  {
    const lab = tex(ctx,256,96,(gg,w,h)=>{
      gg.fillStyle='#8f887a'; gg.fillRect(0,0,w,h);
      gg.fillStyle='#cfc8b4'; gg.fillRect(8,8,w-16,h-16);
      gg.fillStyle='#332c22'; gg.font='600 24px Georgia, serif'; gg.textAlign='center';
      gg.fillText('DISCOVERY · BUNDLE', w/2, 42);
      gg.font='18px Georgia, serif'; gg.fillStyle='#6d675c'; gg.fillText('DRAFT — NOT FILED', w/2, 72);
    });
    inst(ctx,new T.BoxGeometry(0.52,0.34,0.40), M(0x6e6152,.95), 5,(i,d)=>{
      d.position.set(-8.10 + (i>2?0.56:0), 0.17 + (i%3)*0.35, 11.90 - (i>2?0.10:0));
      d.rotation.y = (i%3-1)*0.09;
    });
    pic(ctx,lab,-8.10,0.90,11.69,0.06,0.40,0.15);
  }
}

/* ============================================================================
   OBAMA — room [-15,-12.5 → -11,-2] · door lane x -13.9..-12.1
   Story: he still runs something, and the seal on the floor says which one.
   48 draw calls · bbox x -14.80..-11.18, z -10.61..-2.62
   ========================================================================= */
export function dressObama(ctx){
  const T = ctx.THREE, p = pal(ctx), { B, M, world } = ctx;

  /* --- the seal, laid into the navy oval that is already there --- */
  {
    const seal = tex(ctx,512,512,(g,w,h)=>{
      const C = 256;
      g.fillStyle='#1a2032'; g.beginPath(); g.arc(C,C,252,0,6.3); g.fill();
      g.strokeStyle='#b59a52'; g.lineWidth=7; g.beginPath(); g.arc(C,C,238,0,6.3); g.stroke();
      g.lineWidth=3; g.beginPath(); g.arc(C,C,196,0,6.3); g.stroke();
      g.fillStyle='#d9c26a';                                                  // the ring of stars
      for(let i=0;i<40;i++){ const a=i/40*6.283, x=C+Math.cos(a)*217, y=C+Math.sin(a)*217;
        g.beginPath();
        for(let k=0;k<5;k++){ const aa=a+k*1.2566;
          g.lineTo(x+Math.cos(aa)*6, y+Math.sin(aa)*6);
          g.lineTo(x+Math.cos(aa+0.628)*2.6, y+Math.sin(aa+0.628)*2.6); }
        g.closePath(); g.fill(); }
      g.fillStyle='#cfc8b4'; g.font='700 25px Georgia, serif'; g.textAlign='center';
      const ring=(txt,r,a0,dir)=>{ const step=0.052;
        for(let i=0;i<txt.length;i++){ const a=a0+dir*(i-(txt.length-1)/2)*step;
          g.save(); g.translate(C+Math.cos(a)*r, C+Math.sin(a)*r);
          g.rotate(a + (dir>0?Math.PI/2:-Math.PI/2)); g.fillText(txt[i],0,0); g.restore(); } };
      ring('SEAL OF THE RESIDENT', 172, -Math.PI/2, 1);
      ring('OF THIS FLOOR', 172, Math.PI/2, -1);
      g.fillStyle='#b59a52';                                                  // the bird
      g.beginPath(); g.moveTo(C,180); g.bezierCurveTo(C+34,196, C+40,246, C+16,300);
      g.bezierCurveTo(C,326, C-16,326, C-16,300); g.bezierCurveTo(C-40,246, C-34,196, C,180); g.fill();
      [-1,1].forEach(s=>{
        g.fillStyle='#b59a52'; g.beginPath();
        g.moveTo(C+s*16,208); g.bezierCurveTo(C+s*90,182, C+s*146,206, C+s*162,244);
        g.bezierCurveTo(C+s*120,238, C+s*76,244, C+s*24,252); g.closePath(); g.fill();
        g.fillStyle='#9d8443'; g.beginPath();
        g.moveTo(C+s*24,246); g.bezierCurveTo(C+s*80,244, C+s*120,250, C+s*156,252);
        g.lineTo(C+s*26,262); g.closePath(); g.fill(); });
      g.fillStyle='#cfc8b4'; g.beginPath(); g.arc(C,192,26,0,6.3); g.fill();   // the head
      g.fillStyle='#d9c26a'; g.beginPath(); g.moveTo(C+24,192); g.lineTo(C+46,199); g.lineTo(C+24,204); g.fill();
      g.fillStyle='#e4e0d2'; g.fillRect(C-30,236,60,34);                       // the shield
      g.fillStyle='#8a2f2a'; for(let i=0;i<5;i++) g.fillRect(C-30+i*12,236,6,34);
      g.fillStyle='#1a2032'; g.fillRect(C-30,224,60,14);
      g.fillStyle='#e4e0d2'; g.beginPath(); g.moveTo(C-30,270); g.lineTo(C+30,270); g.lineTo(C,300); g.closePath(); g.fill();
      g.fillStyle='#8a2f2a'; g.beginPath(); g.moveTo(C-18,270); g.lineTo(C+18,270); g.lineTo(C,290); g.closePath(); g.fill();
      g.strokeStyle='#4a6a5e'; g.lineWidth=5;                                  // branch and arrows
      g.beginPath(); g.moveTo(C-40,300); g.quadraticCurveTo(C-90,286, C-124,252); g.stroke();
      g.strokeStyle='#9d8443'; g.lineWidth=4;
      for(let i=0;i<3;i++){ g.beginPath(); g.moveTo(C+40,300); g.lineTo(C+120+i*6, 254-i*10); g.stroke(); }
    });
    const m = new T.Mesh(new T.CircleGeometry(1,64), new T.MeshStandardMaterial({ map:seal, roughness:.9,
      transparent:true, polygonOffset:true, polygonOffsetFactor:-2 }));
    m.rotation.x = -Math.PI/2; m.scale.set(1.30,0.95,1); m.position.set(-13,0.017,-8.60);
    m.receiveShadow = true; world.add(m);
  }

  /* --- the lectern, angled at the door: he is always about to say something --- */
  {
    const g = new T.Group(); g.position.set(-14.10,0,-5.05); g.rotation.y = 0.62; world.add(g);
    ctx.B(0.50,0.05,0.42, M(0x2b2013,.6,.12), 0,0.025,0, 0, g);
    ctx.B(0.26,1.02,0.24, M(0x2b2013,.6,.12), 0,0.53,0, 0, g);               // the column
    ctx.B(0.62,0.09,0.46, M(0x2b2013,.6,.12), 0,1.08,0, 0, g);
    const top = ctx.B(0.60,0.045,0.44, M(0x3a2c1e,.62,.12), 0,1.13,-0.02, 0, g); top.rotation.x = -0.26;
    ctx.B(0.60,0.035,0.05, M(0x2b2013,.6,.12), 0,1.115,0.20, 0, g, false);
    /* the seal plate on the front — the smaller sibling of the one on the rug */
    const plate = tex(ctx,192,192,(gg)=>{
      gg.fillStyle='#1a2032'; gg.beginPath(); gg.arc(96,96,92,0,6.3); gg.fill();
      gg.strokeStyle='#b59a52'; gg.lineWidth=5; gg.beginPath(); gg.arc(96,96,84,0,6.3); gg.stroke();
      gg.fillStyle='#b59a52'; gg.beginPath(); gg.moveTo(96,52); gg.bezierCurveTo(120,64,124,102,108,132);
      gg.bezierCurveTo(96,146,84,146,84,132); gg.bezierCurveTo(68,102,72,64,96,52); gg.fill();
      [-1,1].forEach(s=>{ gg.beginPath(); gg.moveTo(96+s*8,74); gg.bezierCurveTo(96+s*40,60,96+s*64,74,96+s*72,94);
        gg.bezierCurveTo(96+s*50,92,96+s*30,96,96+s*12,100); gg.closePath(); gg.fill(); });
      gg.fillStyle='#cfc8b4'; gg.beginPath(); gg.arc(96,62,12,0,6.3); gg.fill();
      gg.fillStyle='#e4e0d2'; gg.fillRect(84,92,24,16);
      gg.fillStyle='#8a2f2a'; for(let i=0;i<3;i++) gg.fillRect(84+i*9,92,4,16);
    });
    const pl = new T.Mesh(new T.CircleGeometry(0.16,32), new T.MeshStandardMaterial({ map:plate, roughness:.6 }));
    pl.position.set(0,0.68,0.126); g.add(pl);
    /* two prompter panes on rods — the silhouette everyone recognises */
    [[-0.52,-0.22],[0.52,0.22]].forEach(q=>{
      ctx.Cyl(0.014,0.92, p.CHAR, q[0],0.46,0.10, g, 6).castShadow = false;
      const pane = ctx.B(0.28,0.34,0.012, p.GLASS, q[0],0.98,0.12, q[1], g, false);
      pane.rotation.x = 0.42; pane.receiveShadow = false;
    });
    const sheet = ctx.B(0.28,0.014,0.20, ctx.PAPER, -0.06,1.155,-0.03, 0.06, g, false); sheet.rotation.x = -0.26;
    const rl = ctx.Cyl(0.015,0.16, p.CHAR, 0.22,1.20,-0.14, g, 6); rl.rotation.x = 0.5; rl.castShadow = false;
    const bulb = new T.Mesh(new T.SphereGeometry(0.032,10,8), glowMat(ctx,0xffd9a0,0xffc37a,1.2));
    bulb.position.set(0.22,1.27,-0.09); g.add(bulb);
  }

  /* --- the folded flag in its case: the one that already came home --- */
  {
    const g = new T.Group(); g.position.set(-11.60,0,-6.45); g.rotation.y = -0.35; world.add(g);
    ctx.B(0.56,0.62,0.44, ctx.WOODD, 0,0.31,0, 0, g);                        // side table
    ctx.B(0.62,0.04,0.50, ctx.WOODL, 0,0.64,0, 0, g);
    const caseM = new T.Mesh(new T.CylinderGeometry(0.20,0.20,0.10,3,1,false), M(0x3a2f24,.78,.1));
    caseM.rotation.x = Math.PI/2; caseM.rotation.z = Math.PI; caseM.position.set(0,0.78,0);
    caseM.castShadow = true; g.add(caseM);
    const flag = new T.Mesh(new T.CylinderGeometry(0.155,0.155,0.055,3,1,false), p.NAVY);
    flag.rotation.x = Math.PI/2; flag.rotation.z = Math.PI; flag.position.set(0,0.775,0.03); g.add(flag);
    inst(ctx,new T.CircleGeometry(0.008,5), M(0xd9c26a,.8), 7,(i,d)=>{
      d.position.set(-0.05+(i%4)*0.033, 0.755+((i/4)|0)*0.035, 0.059);
    },false,g);
    const gl = ctx.B(0.34,0.30,0.008, p.GLASS, 0,0.78,0.056, 0, g, false); gl.receiveShadow = false;
  }

  /* --- the briefing cart: the day arrives in binders, not in a feed --- */
  {
    const g = new T.Group(); g.position.set(-14.35,0,-6.90); g.rotation.y = 0.20; world.add(g);
    ctx.B(0.82,0.045,0.52, p.SLATE, 0,0.50,0, 0, g);
    ctx.B(0.82,0.045,0.52, p.SLATE, 0,0.24,0, 0, g);
    ctx.B(0.82,0.32,0.045, p.SLATE, 0,0.68,-0.24, 0, g);
    [[-0.34,-0.20],[0.34,-0.20],[-0.34,0.20],[0.34,0.20]].forEach(q=>{
      ctx.Cyl(0.024,0.48, p.CHAR, q[0],0.26,q[1], g, 6).castShadow = false;
      ctx.Cyl(0.048,0.035, p.JET, q[0],0.05,q[1], g, 8).castShadow = false;
    });
    inst(ctx,new T.BoxGeometry(0.075,0.30,0.24), M(0x141416,.9), 8,(i,d)=>{
      d.position.set(-0.30+(i%4)*0.085, (i<4?0.675:0.415), 0.02);
      d.rotation.z = (i%4===3)?0.16:0;
    },false,g);
    const band = tex(ctx,128,128,(gg)=>{ gg.fillStyle='#141416'; gg.fillRect(0,0,128,128);
      gg.fillStyle='#b59a52'; gg.fillRect(0,44,128,40);
      gg.fillStyle='#141416'; gg.font='700 26px Georgia, serif'; gg.textAlign='center'; gg.fillText('PDB',64,74); });
    pic(ctx,band,-14.20,0.98,-6.66, 0.20, 0.30, 0.30);
    inst(ctx,new T.BoxGeometry(0.24,0.055,0.30), M(0x141416,.9), 3,(i,d)=>{   // three more, open, on the desk
      d.position.set(-13.62+i*0.02, 0.805+i*0.058, -10.45); d.rotation.y = (i-1)*0.10;
    });
  }

  /* --- the hoop and the ball: he plays, he wins, and he mentions it --- */
  {
    const g = new T.Group(); g.position.set(-11.52,0,-3.05); g.rotation.y = -Math.PI/2; world.add(g);
    ctx.B(0.62,0.10,0.50, p.JET, 0,0.05,0.16, 0, g);                         // the weighted base
    ctx.Cyl(0.045,2.30, p.CHAR, 0,1.15,0.10, g, 8);
    const brace = ctx.Cyl(0.028,0.50, p.CHAR, 0,1.90,0.28, g, 6); brace.rotation.x = 0.7; brace.castShadow = false;
    ctx.B(0.86,0.58,0.035, M(0xd8d2c2,.86), 0,2.24,-0.05, 0, g);             // backboard
    ctx.B(0.34,0.24,0.008, M(0x8a2f2a,.8), 0,2.16,-0.030, 0, g, false);
    const rim = new T.Mesh(new T.TorusGeometry(0.15,0.016,6,18), M(0xc25a3a,.55,.35));
    rim.rotation.x = Math.PI/2; rim.position.set(0,1.98,-0.18); rim.castShadow = true; g.add(rim);
    const net = new T.Mesh(new T.CylinderGeometry(0.145,0.085,0.22,14,1,true),
      new T.MeshStandardMaterial({ color:0xd8d2c2, roughness:.95, transparent:true, opacity:.42, side:T.DoubleSide, wireframe:true }));
    net.position.set(0,1.87,-0.18); g.add(net);
    const ball = new T.Mesh(new T.SphereGeometry(0.118,18,14), M(0xa8552c,.85));
    ball.position.set(-11.65,0.118,-4.35); ball.castShadow = true; world.add(ball);
    inst(ctx,new T.TorusGeometry(0.119,0.004,4,20), p.JET, 3,(i,d)=>{        // the seams
      d.position.set(-11.65,0.118,-4.35);
      d.rotation.set(i===0?0:Math.PI/2, i===2?Math.PI/2:0, 0);
    });
  }

  /* --- the bust: the man he keeps in the room to argue with --- */
  {
    B(0.34,1.06,0.34, M(0x232327,.72), -14.45,0.53,-3.10, 0, world);
    const h = new T.Mesh(new T.SphereGeometry(0.105,16,13), M(0x6b5a3c,.5,.45));
    h.scale.set(0.92,1.12,1); h.position.set(-14.45,1.21,-3.10); h.castShadow = true; world.add(h);
    const sh = new T.Mesh(new T.CylinderGeometry(0.085,0.145,0.20,14), M(0x6b5a3c,.5,.45));
    sh.position.set(-14.45,1.10,-3.10); sh.castShadow = true; world.add(sh);
    b0(ctx,0.055,0.032,0.045, M(0x6b5a3c,.5,.45), -14.38,1.20,-3.02, 0.25);  // brow and nose
    b0(ctx,0.13,0.006,0.028, p.GOLD, -14.45,1.075,-2.94, 0);
  }
}

/* ============================================================================
   JANNA — room [1,2 → 9,12.5] · door lane x 4.1..5.9
   Story: the mail room, and the two trays that are the whole doctrine —
   IN is heaped, OUT is empty until he says YES.
   57 draw calls · bbox x 1.85..8.01, z 3.24..11.91
   (Kept clear of Buffett's cash pallet, which reaches x 1.4 at z 6.1..6.7.)
   ========================================================================= */
export function dressJanna(ctx){
  const T = ctx.THREE, p = pal(ctx), { M, world } = ctx;

  /* --- the sorting counter --- */
  const CT = 0.92;                                                            // counter top height
  {
    const g = new T.Group(); g.position.set(4.20,0,11.50); g.rotation.y = Math.PI; world.add(g);
    ctx.B(3.20,0.86,0.72, ctx.WOODD, 0,0.43,0, 0, g);
    ctx.B(3.32,0.06,0.80, ctx.WOODL, 0,CT,0, 0, g);
    ctx.B(3.20,0.62,0.05, M(0x2f2921,.9), 0,1.26,-0.36, 0, g);               // the back board
    inst(ctx,new T.BoxGeometry(3.16,0.026,0.20), ctx.WOODL, 3,(i,d)=>{ d.position.set(0,1.06+i*0.24,-0.27); },false,g);
    inst(ctx,new T.BoxGeometry(0.024,0.22,0.20), ctx.WOODL, 15,(i,d)=>{ d.position.set(-1.40+(i%5)*0.70, 1.17+((i/5)|0)*0.24, -0.27); },false,g);
    inst(ctx,new T.BoxGeometry(0.20,0.016,0.14), ctx.PAPER, 11,(i,d)=>{      // sorted, waiting
      d.position.set(-1.28+(i%5)*0.68, 1.085+((i/5)|0)*0.24, -0.22); d.rotation.x = 0.4; d.rotation.y = (i%3-1)*0.08;
    },false,g);
  }
  /* --- the two trays: this is the whole org chart in two objects --- */
  {
    const tray = (x, full)=>{
      ctx.B(0.34,0.018,0.26, M(0x45454c,.7,.25), x,CT+0.04,11.42, 0, world, false);
      ctx.B(0.34,0.055,0.016, M(0x45454c,.7,.25), x,CT+0.07,11.30, 0, world, false);
      ctx.B(0.016,0.055,0.26, M(0x45454c,.7,.25), x-0.16,CT+0.07,11.42, 0, world, false);
      ctx.B(0.016,0.055,0.26, M(0x45454c,.7,.25), x+0.16,CT+0.07,11.42, 0, world, false);
      if(full) inst(ctx,new T.BoxGeometry(0.30,0.010,0.22), ctx.PAPER, 12,(i,d)=>{
        d.position.set(x+((i%3)-1)*0.012, CT+0.055+i*0.012, 11.42+((i%4)-1.5)*0.010);
        d.rotation.y = (i%5-2)*0.055;
      });
    };
    tray(2.90, true); tray(5.25, false);
    const labs = tex(ctx,256,128,(gg,w,h)=>{
      gg.fillStyle='#1c1a17'; gg.fillRect(0,0,w,h);
      gg.fillStyle='#cfc8b4'; gg.font='700 46px Futura, Helvetica, sans-serif'; gg.textAlign='center';
      gg.fillText('IN', w/2, 58);
      gg.font='16px Futura, Helvetica, sans-serif'; gg.fillStyle='#8f887a';
      gg.fillText('everything lands here first', w/2, 92);
    });
    const labs2 = tex(ctx,256,128,(gg,w,h)=>{
      gg.fillStyle='#1c1a17'; gg.fillRect(0,0,w,h);
      gg.fillStyle='#cfc8b4'; gg.font='700 46px Futura, Helvetica, sans-serif'; gg.textAlign='center';
      gg.fillText('OUT', w/2, 58);
      gg.font='16px Futura, Helvetica, sans-serif'; gg.fillStyle='#8f887a';
      gg.fillText('empty until he says YES', w/2, 92);
    });
    pic(ctx,labs, 2.90, CT+0.10, 11.283, Math.PI, 0.30, 0.15);
    pic(ctx,labs2,5.25, CT+0.10, 11.283, Math.PI, 0.30, 0.15);
  }
  /* --- the franking machine, the scale, the date stamp --- */
  {
    ctx.B(0.34,0.22,0.28, p.SLATE, 3.78,CT+0.14,11.46, -0.10, world);        // franking machine
    ctx.B(0.30,0.05,0.10, p.CHAR, 3.78,CT+0.02,11.32, -0.10, world, false);  // the feed slot
    const cnt = tex(ctx,128,64,(gg)=>{ gg.fillStyle='#0e100e'; gg.fillRect(0,0,128,64);
      gg.fillStyle='#d9a05e'; gg.font='600 30px Menlo, monospace'; gg.textAlign='center'; gg.fillText('000000',64,42); });
    const cm = new T.MeshStandardMaterial({ map:cnt, emissive:0xffffff, emissiveMap:cnt, emissiveIntensity:.5, roughness:.42 });
    ctx.B(0.20,0.08,0.012, cm, 3.78,CT+0.20,11.60, -0.10, world, false);
    ctx.SCREENS.push({ m:cm, base:.5 });
    /* the postal scale */
    ctx.Cyl(0.10,0.03, p.CHAR, 5.62,CT+0.045,11.46, world, 14).castShadow = false;
    ctx.Cyl(0.022,0.16, p.CHAR, 5.62,CT+0.13,11.46, world, 8).castShadow = false;
    ctx.B(0.22,0.014,0.18, M(0x9a9aa2,.35,.7), 5.62,CT+0.22,11.46, 0.2, world, false);
    const face = tex(ctx,128,128,(gg)=>{ gg.fillStyle='#cfc8b4'; gg.beginPath(); gg.arc(64,64,60,0,6.3); gg.fill();
      gg.strokeStyle='#332c22'; gg.lineWidth=3;
      for(let i=0;i<24;i++){ const a=-2.6+i*0.24; gg.beginPath();
        gg.moveTo(64+Math.cos(a)*52,64+Math.sin(a)*52); gg.lineTo(64+Math.cos(a)*(i%4?44:38),64+Math.sin(a)*(i%4?44:38)); gg.stroke(); }
      gg.strokeStyle='#8a2f2a'; gg.lineWidth=4; gg.beginPath(); gg.moveTo(64,64); gg.lineTo(64+Math.cos(-1.5)*40,64+Math.sin(-1.5)*40); gg.stroke();
      gg.fillStyle='#6d675c'; gg.font='600 13px Futura, sans-serif'; gg.textAlign='center'; gg.fillText('GRAMS',64,96); });
    pic(ctx,face, 5.62, CT+0.13, 11.355, Math.PI, 0.14, 0.14);
    /* the date-stamp carousel and its pad */
    ctx.Cyl(0.06,0.10, p.JET, 4.62,CT+0.08,11.44, world, 12).castShadow = false;
    ctx.Cyl(0.012,0.09, p.KRAFT, 4.62,CT+0.17,11.44, world, 6).castShadow = false;
    ctx.B(0.16,0.028,0.11, M(0x8a2f2a,.8), 4.86,CT+0.045,11.44, 0.2, world, false);
  }
  /* --- kraft paper, twine and stamps, on their own stand --- */
  {
    const g = new T.Group(); g.position.set(6.45,0,11.62); g.rotation.y = -0.2; world.add(g);
    ctx.B(0.62,0.80,0.46, ctx.WOODD, 0,0.40,0, 0, g);
    inst(ctx,new T.BoxGeometry(0.05,0.28,0.05), p.CHAR, 2,(i,d)=>{ d.position.set(-0.26+i*0.52,0.94,0); },false,g);
    const roll = ctx.Cyl(0.13,0.50, M(0xa8895c,.94), 0,0.98,0, g, 16); roll.rotation.z = Math.PI/2;
    const sheet2 = ctx.B(0.44,0.30,0.006, M(0xb59567,.94), 0,0.70,0.14, 0, g, false); sheet2.rotation.x = -0.25;
    const twine = new T.Mesh(new T.SphereGeometry(0.075,14,10), M(0xc2b898,.96));
    twine.position.set(0.20,0.87,0.16); twine.castShadow = true; g.add(twine);
    ctx.Cyl(0.05,0.03, M(0x9d8443,.85), -0.20,0.86,0.14, g, 12).castShadow = false;   // roll of stamps
  }
  /* --- the pneumatic tube station: it still works, nobody uses it --- */
  {
    const g = new T.Group(); g.position.set(7.70,0,11.40); g.rotation.y = -0.35; world.add(g);
    ctx.B(0.50,0.10,0.44, p.JET, 0,0.05,0, 0, g);
    ctx.Cyl(0.055,1.90, p.BRASS, -0.16,0.99,0, g, 10);
    const tube = new T.Mesh(new T.CylinderGeometry(0.075,0.075,1.62,14,1,true), p.GLASS);
    tube.position.set(0.10,1.02,0); g.add(tube);
    inst(ctx,new T.TorusGeometry(0.078,0.010,5,14), p.BRASS, 4,(i,d)=>{ d.position.set(0.10,0.28+i*0.50,0); d.rotation.x=Math.PI/2; },false,g);
    const cap = ctx.Cyl(0.058,0.20, M(0xb59567,.7), 0.10,1.28,0, g, 12); cap.castShadow = false;   // the carrier, stuck
    ctx.Cyl(0.062,0.03, p.BRASS, 0.10,1.39,0, g, 12).castShadow = false;
    ctx.Cyl(0.062,0.03, p.BRASS, 0.10,1.17,0, g, 12).castShadow = false;
    ctx.B(0.30,0.14,0.28, p.BRASS, 0.10,0.24,0.02, 0, g);                    // the receiving basket
    ctx.B(0.16,0.006,0.05, p.GOLD, -0.16,1.72,0.06, 0, g, false);            // the plaque
  }
  /* --- the sack of what arrived overnight --- */
  {
    const s = new T.Mesh(new T.SphereGeometry(0.30,16,12), M(0x8f887a,.98));
    s.scale.set(1,0.86,0.9); s.position.set(2.30,0.26,10.10); s.castShadow = true; world.add(s);
    cyl0(ctx,0.10,0.20, M(0x8f887a,.98), 2.30,0.55,10.10, 12);
    inst(ctx,new T.TorusGeometry(0.10,0.010,4,12), p.RUST, 2,(i,d)=>{ d.position.set(2.30,0.52+i*0.04,10.10); d.rotation.x=Math.PI/2; });
    b0(ctx,0.20,0.012,0.14, ctx.PAPER, 2.58,0.008,9.82, 0.5);
    b0(ctx,0.20,0.012,0.14, ctx.PAPER, 2.70,0.008,10.02, -0.3);
  }
  /* --- the clock, on a bracket off the mail shelving (the only fixing available) --- */
  {
    b0(ctx,0.42,0.045,0.045, p.CHAR, 2.06,1.72,8.60, 0);
    b0(ctx,0.045,0.22,0.045, p.CHAR, 2.24,1.62,8.60, 0);
    const cf = tex(ctx,256,256,(gg)=>{
      gg.fillStyle='#1c1a17'; gg.beginPath(); gg.arc(128,128,124,0,6.3); gg.fill();
      gg.fillStyle='#cfc8b4'; gg.beginPath(); gg.arc(128,128,110,0,6.3); gg.fill();
      gg.fillStyle='#1c1a17';
      for(let i=0;i<12;i++){ const a=i/12*6.283; gg.save(); gg.translate(128+Math.cos(a)*92,128+Math.sin(a)*92);
        gg.rotate(a); gg.fillRect(-3,-9,6,18); gg.restore(); }
      gg.strokeStyle='#1c1a17'; gg.lineWidth=8; gg.lineCap='round';
      gg.beginPath(); gg.moveTo(128,128); gg.lineTo(128+Math.cos(-0.9)*58,128+Math.sin(-0.9)*58); gg.stroke();
      gg.lineWidth=5; gg.beginPath(); gg.moveTo(128,128); gg.lineTo(128+Math.cos(1.4)*86,128+Math.sin(1.4)*86); gg.stroke();
      gg.fillStyle='#8a2f2a'; gg.beginPath(); gg.arc(128,128,8,0,6.3); gg.fill();
      gg.fillStyle='#6d675c'; gg.font='600 15px Futura, sans-serif'; gg.textAlign='center'; gg.fillText('LAST POST 16:30',128,186);
    });
    ctx.Cyl(0.23,0.05, p.CHAR, 2.30,1.72,8.60, world, 24).rotation.z = Math.PI/2;
    pic(ctx,cf, 2.335, 1.72, 8.60, Math.PI/2, 0.42, 0.42);
  }
  /* --- the collection board, on an easel where you see it leaving --- */
  {
    const e = easel(ctx, 7.10, 3.60, -0.70, 1.50);
    const bt = tex(ctx,384,288,(gg,w,h)=>{
      gg.fillStyle='#1e2119'; gg.fillRect(0,0,w,h);
      gg.strokeStyle='#5c5a4e'; gg.lineWidth=3; gg.strokeRect(10,10,w-20,h-20);
      gg.fillStyle='#cfc8b4'; gg.font='700 26px Futura, Helvetica, sans-serif'; gg.textAlign='center';
      gg.fillText('COLLECTIONS', w/2, 52);
      gg.fillStyle='#5c5a4e'; gg.fillRect(60,64,w-120,2);
      gg.textAlign='left'; gg.font='19px Menlo, monospace'; gg.fillStyle='#a89f8a';
      [['09:00','sorted'],['11:30','walked to ATG'],['14:00','walked to ATG'],['16:30','last post']].forEach((r,i)=>{
        gg.fillText(r[0], 46, 106+i*32); gg.fillText(r[1], 150, 106+i*32); });
      gg.fillStyle='#d98a4a'; gg.font='700 20px Futura, sans-serif'; gg.textAlign='center';
      gg.fillText('NOTHING LEAVES WITHOUT A YES', w/2, h-32);
    });
    const bf = new T.Mesh(new T.PlaneGeometry(0.76,0.57), new T.MeshStandardMaterial({ map:bt, roughness:.78 }));
    bf.position.set(0,0.86,0.155); bf.rotation.x = -0.07; e.add(bf);
    ctx.B(0.80,0.62,0.03, M(0x2f2921,.9), 0,0.86,0.14, 0, e);
  }
}

/* ============================================================================
   LOUDFAIR — room [9,2 → 17,12.5] · door lane x 12.1..13.9
   Story: the two words on the wall, made into two objects you can pick up.
   60 draw calls · bbox x 9.14..16.04, z 2.31..12.26
   ========================================================================= */
export function dressLoudfair(ctx){
  const T = ctx.THREE, p = pal(ctx), { M, world } = ctx;

  /* --- LOUD and FAIR, on one plinth, in the order the sign says --- */
  {
    ctx.B(0.86,0.92,0.56, M(0x232327,.72), 10.30,0.46,3.60, 0, world);
    ctx.B(0.94,0.035,0.64, p.JET, 10.30,0.937,3.60, 0, world);
    /* the megaphone */
    const g = new T.Group(); g.position.set(10.12,1.02,3.62); g.rotation.y = -0.55; g.rotation.z = 0.30; world.add(g);
    const horn = new T.Mesh(new T.CylinderGeometry(0.055,0.155,0.30,16,1,true), M(0xd98a4a,.6));
    horn.rotation.z = Math.PI/2; horn.position.set(0.06,0.10,0); horn.castShadow = true; g.add(horn);
    ctx.Cyl(0.057,0.10, p.JET, -0.13,0.10,0, g, 12).rotation.z = Math.PI/2;
    ctx.B(0.05,0.11,0.045, p.JET, -0.10,0.03,0, 0, g, false);                // the grip
    ctx.B(0.07,0.03,0.05, p.CHAR, -0.05,0.005,0, 0, g, false);               // the trigger
    inst(ctx,new T.TorusGeometry(0.155,0.008,4,16), M(0xb8763a,.6), 1,(i,d)=>{ d.position.set(0.21,0.10,0); d.rotation.y=Math.PI/2; },false,g);
    /* the balance, dead level */
    const s = new T.Group(); s.position.set(10.62,0.955,3.60); s.rotation.y = 0.30; world.add(s);
    ctx.Cyl(0.10,0.025, p.BRASS, 0,0.012,0, s, 14);
    ctx.Cyl(0.014,0.30, p.BRASS, 0,0.16,0, s, 8);
    ctx.B(0.42,0.012,0.012, p.BRASS, 0,0.31,0, 0, s, false);
    [-0.19,0.19].forEach(dx=>{
      inst(ctx,new T.BoxGeometry(0.002,0.10,0.002), p.BRASS, 2,(i,d)=>{ d.position.set(dx,0.255,-0.02+i*0.04); d.rotation.z=(i?0.1:-0.1); },false,s);
      const pan = ctx.Cyl(0.055,0.008, p.BRASS, dx,0.205,0, s, 14); pan.castShadow = false;
    });
    const plate = tex(ctx,256,64,(gg,w,h)=>{ gg.fillStyle='#141416'; gg.fillRect(0,0,w,h);
      gg.fillStyle='#b59a52'; gg.font='700 30px Futura, Helvetica, sans-serif'; gg.textAlign='center';
      gg.fillText('L O U D   ·   F A I R', w/2, 42); });
    pic(ctx,plate, 10.30, 0.72, 3.885, 0, 0.62, 0.155);
  }

  /* --- the merch rail: an agency sells itself before it sells anything --- */
  {
    const g = new T.Group(); g.position.set(9.90,0,11.50); g.rotation.y = 0.30; world.add(g);
    [[-0.52],[0.52]].forEach(q=>{
      ctx.B(0.42,0.045,0.42, p.CHAR, q[0],0.022,0, 0, g, false);
      ctx.Cyl(0.024,1.44, p.STEEL, q[0],0.74,0, g, 8);
      inst(ctx,new T.CylinderGeometry(0.035,0.035,0.026,10), p.JET, 2,(i,d)=>{ d.position.set(q[0]-0.14+i*0.28,0.026,0); d.rotation.x=Math.PI/2; },false,g);
    });
    const bar = ctx.Cyl(0.016,1.10, p.STEEL, 0,1.44,0, g, 8); bar.rotation.z = Math.PI/2;
    inst(ctx,new T.BoxGeometry(0.10,0.020,0.020), p.STEEL, 7,(i,d)=>{        // the hangers
      d.position.set(-0.42+i*0.14, 1.40, 0); d.rotation.z = 1.1;
    },false,g);
    inst(ctx,new T.BoxGeometry(0.30,0.62,0.09), M(0x2b2b30,.96), 7,(i,d)=>{  // the garments
      d.position.set(-0.42+i*0.14, 1.02, 0.01+((i%3)-1)*0.02); d.rotation.y = (i%4-1.5)*0.10;
      d.scale.set(1, 0.86+((i*3)%4)*0.09, 1);
      return [0x2b2b30,0x3a342c,0x1e2436,0x4f5a4e][i%4];
    },true,g);
    ctx.B(0.36,0.28,0.26, M(0x8a7a5c,.9), 0.66,0.14,0.16, 0.3, g);           // the box they came in
  }

  /* --- the moodboard, on its own legs because there is no wall to take it --- */
  {
    const g = new T.Group(); g.position.set(12.60,0,12.15); g.rotation.y = Math.PI; world.add(g);
    ctx.B(3.00,0.10,0.14, p.CHAR, 0,2.02,0, 0, g);
    ctx.B(3.00,0.08,0.14, p.CHAR, 0,0.34,0, 0, g);
    ctx.B(0.10,1.76,0.14, p.CHAR, -1.50,1.18,0, 0, g);
    ctx.B(0.10,1.76,0.14, p.CHAR,  1.50,1.18,0, 0, g);
    ctx.B(0.42,0.05,0.46, p.CHAR, -1.50,0.025,0.14, 0, g, false);            // the feet
    ctx.B(0.42,0.05,0.46, p.CHAR,  1.50,0.025,0.14, 0, g, false);
    const board = tex(ctx,1024,640,(gg,w,h)=>{
      gg.fillStyle='#1a1a1d'; gg.fillRect(0,0,w,h);
      const cols = ['#c2543a','#d98a4a','#d9c26a','#8291c9','#4a6a5e','#cfc8b4'];
      for(let r=0;r<3;r++) for(let c=0;c<4;c++){
        const x=34+c*246, y=30+r*200, ww=214, hh=170, k=r*4+c;
        gg.fillStyle='#e4e0d2'; gg.fillRect(x-6,y-6,ww+12,hh+12);
        gg.fillStyle='#26262a'; gg.fillRect(x,y,ww,hh);
        if(k%4===0){ gg.strokeStyle='#8f887a'; gg.lineWidth=3;               // a wireframe
          gg.strokeRect(x+14,y+14,ww-28,26); gg.strokeRect(x+14,y+52,96,hh-70); gg.strokeRect(x+120,y+52,ww-134,50);
          gg.fillStyle='#8f887a'; gg.fillRect(x+22,y+22,52,10); }
        else if(k%4===1){ gg.fillStyle=cols[k%cols.length];                  // a type specimen
          gg.font='700 74px Futura, Helvetica, sans-serif'; gg.fillText('Aa', x+18, y+96);
          gg.fillStyle='#8f887a'; gg.font='17px Menlo, monospace'; gg.fillText('display / 74 / -2%', x+20, y+134); }
        else if(k%4===2){ for(let i=0;i<6;i++){ gg.fillStyle=cols[(k+i)%cols.length];   // a palette strip
            gg.fillRect(x+14+i*32, y+40, 28, hh-80); } }
        else { gg.strokeStyle=cols[k%cols.length]; gg.lineWidth=5;           // a gestural mark
          gg.beginPath(); gg.moveTo(x+24,y+hh-30);
          gg.bezierCurveTo(x+80,y+20, x+150,y+hh-20, x+ww-24,y+34); gg.stroke(); }
        gg.fillStyle='#6d675c'; gg.font='13px Menlo, monospace'; gg.fillText('v'+(k+1), x+ww-38, y+hh-12);
      }
    });
    const f = new T.Mesh(new T.PlaneGeometry(2.88,1.68), new T.MeshStandardMaterial({ map:board, roughness:.8 }));
    f.position.set(0,1.18,0.08); g.add(f);
    inst(ctx,new T.BoxGeometry(0.05,0.032,0.026), p.JET, 12,(i,d)=>{         // the clips
      d.position.set(-1.24+(i%4)*0.82, 2.00-((i/4)|0)*0.56, 0.10);
    },false,g);
  }

  /* --- the espresso counter: where the work actually gets decided --- */
  {
    const g = new T.Group(); g.position.set(15.30,0,11.95); g.rotation.y = Math.PI; world.add(g);
    ctx.B(1.40,0.86,0.55, ctx.WOODD, 0,0.43,0, 0, g);
    ctx.B(1.48,0.055,0.62, M(0x2b2b30,.6,.3), 0,0.885,0, 0, g);
    ctx.B(0.46,0.34,0.34, M(0xb9bcc2,.30,.78), -0.32,1.08,0, 0, g);          // the machine
    ctx.B(0.42,0.09,0.30, p.JET, -0.32,1.29,0, 0, g, false);
    ctx.B(0.10,0.10,0.09, p.CHAR, -0.32,0.955,0.17, 0, g, false);            // group head
    ctx.Cyl(0.028,0.10, p.JET, -0.20,0.955,0.19, g, 8).castShadow = false;   // portafilter handle
    const wand = ctx.Cyl(0.010,0.20, M(0xb9bcc2,.3,.8), -0.52,0.98,0.10, g, 6); wand.rotation.x = 0.5; wand.castShadow = false;
    const gauge = tex(ctx,64,64,(gg)=>{ gg.fillStyle='#cfc8b4'; gg.beginPath(); gg.arc(32,32,30,0,6.3); gg.fill();
      gg.strokeStyle='#8a2f2a'; gg.lineWidth=3; gg.beginPath(); gg.moveTo(32,32); gg.lineTo(50,18); gg.stroke();
      gg.strokeStyle='#332c22'; gg.lineWidth=2; gg.beginPath(); gg.arc(32,32,24,3.4,6.0); gg.stroke(); });
    pic(ctx,gauge, 15.62, 1.12, 11.775, Math.PI, 0.10, 0.10);
    ctx.B(0.22,0.36,0.24, p.CHAR, 0.38,1.09,0, 0, g);                        // the grinder
    ctx.Cyl(0.085,0.16, p.GLASS, 0.38,1.35,0, g, 12).castShadow = false;
    inst(ctx,new T.CylinderGeometry(0.032,0.024,0.055,12), M(0xe4e0d2,.5), 5,(i,d)=>{
      d.position.set(-0.02+(i%3)*0.11, 0.94, -0.14+((i/3)|0)*0.13);
    },false,g);
    ctx.B(0.13,0.03,0.09, M(0x8a7a5c,.9), 0.16,0.928,0.20, 0.3, g, false);   // somebody's biscuit
  }

  /* --- the camera on sticks: they shoot their own work --- */
  {
    const g = new T.Group(); g.position.set(10.50,0,7.40); g.rotation.y = 0.9; world.add(g);
    inst(ctx,new T.BoxGeometry(0.030,1.28,0.030), p.CHAR, 3,(i,d)=>{
      const a = i/3*Math.PI*2; d.position.set(Math.cos(a)*0.22,0.64,Math.sin(a)*0.22);
      d.rotation.z = -Math.cos(a)*0.34; d.rotation.x = Math.sin(a)*0.34;
    },false,g);
    ctx.Cyl(0.035,0.24, p.CHAR, 0,1.34,0, g, 8);
    ctx.B(0.16,0.10,0.12, p.SLATE, 0,1.48,0, 0, g);                          // the head
    ctx.B(0.22,0.15,0.16, p.JET, 0,1.60,0, 0, g);                            // the body
    ctx.Cyl(0.055,0.16, p.JET, 0,1.60,0.14, g, 14).rotation.x = Math.PI/2;   // the lens
    ctx.Cyl(0.058,0.012, M(0x2a2f28,.2,.4), 0,1.60,0.225, g, 14).rotation.x = Math.PI/2;
    const rec = ctx.B(0.016,0.016,0.008, glowMat(ctx,0x222222,0x8a2f2a,1.6), 0.09,1.665,0.082, 0, g, false);
    ctx.BLINKS.push({ m:rec.material, ph:1.9 });
    const arm = ctx.Cyl(0.010,0.26, p.CHAR, -0.10,1.44,-0.10, g, 6); arm.rotation.x = 0.7; arm.rotation.z = 0.5; arm.castShadow = false;
  }

  /* --- pitch boards, leaning where they were dropped --- */
  {
    ctx.B(0.44,0.34,0.42, M(0x8a7a5c,.9), 11.62,0.17,2.62, 0.2, world);      // the crate they travel in
    const bt = tex(ctx,256,320,(gg,w,h)=>{
      gg.fillStyle='#e4e0d2'; gg.fillRect(0,0,w,h);
      gg.fillStyle='#1a1a1d'; gg.fillRect(0,0,w,58);
      gg.fillStyle='#d98a4a'; gg.font='700 27px Futura, Helvetica, sans-serif'; gg.fillText('LOUDFAIR', 16, 40);
      gg.fillStyle='#26262a'; gg.fillRect(16,80,w-32,120);
      gg.fillStyle='#8f887a'; gg.fillRect(16,216,w-32,10); gg.fillRect(16,238,150,10); gg.fillRect(16,260,190,10);
      gg.fillStyle='#4a6a5e'; gg.fillRect(16,286,84,20);
    });
    [[11.05,2.48,0.16],[11.24,2.44,-0.10],[11.42,2.50,0.04]].forEach((q,i)=>{
      const g = new T.Group(); g.position.set(q[0],0.61,q[1]); g.rotation.y = q[2]; g.rotation.x = -0.16; world.add(g);
      ctx.B(0.62,1.16,0.022, M(0xd8d2c2,.94), 0,0,0, 0, g);
      const f = new T.Mesh(new T.PlaneGeometry(0.56,1.06), new T.MeshStandardMaterial({ map:bt, roughness:.8 }));
      f.position.z = 0.014; g.add(f);
    });
  }
}

/* ============================================================================
   ASSANGE — room [19,-12.5 → 25,-2] · door lane (the bars) x 20.8..23.2
   Story: a room he cannot leave, a cat that can, and the paper that keeps him here.
   43 draw calls · bbox x 19.17..24.64, z -12.38..-3.60
   ========================================================================= */
export function dressAssange(ctx){
  const T = ctx.THREE, p = pal(ctx), { M, world } = ctx;

  /* --- the cat, asleep on the blanket: the only one in here on its own terms --- */
  {
    const g = new T.Group(); g.position.set(23.30,0.43,-10.68); g.rotation.y = -0.75; world.add(g);
    const fur = M(0x6b6156,.96);
    const body = new T.Mesh(new T.SphereGeometry(0.115,16,12), fur);
    body.scale.set(1.55,0.80,1.05); body.castShadow = true; body.position.y = 0.088; g.add(body);
    const head = new T.Mesh(new T.SphereGeometry(0.068,14,11), fur);
    head.position.set(0.145,0.105,0.030); head.castShadow = true; g.add(head);
    inst(ctx,new T.ConeGeometry(0.028,0.048,4), fur, 2,(i,d)=>{
      d.position.set(0.135, 0.158, 0.030+(i?0.042:-0.042)); d.rotation.x = (i?0.3:-0.3);
    },false,g);
    const tail = new T.Mesh(new T.TorusGeometry(0.105,0.019,5,16, 3.5), fur);
    tail.rotation.x = Math.PI/2; tail.rotation.z = 1.1; tail.position.set(-0.045,0.055,0.055); g.add(tail);
    inst(ctx,new T.BoxGeometry(0.024,0.004,0.006), p.JET, 2,(i,d)=>{         // eyes, shut
      d.position.set(0.198, 0.115, 0.030+(i?0.032:-0.032)); d.rotation.y = (i?0.3:-0.3);
    },false,g);
    b0(ctx,0.014,0.010,0.014, M(0xa87f70,.9), 23.10,0.535,-10.82, -0.75);    // nose
  }

  /* --- the mattress on the floor: the bed of somebody not staying, for years --- */
  {
    const g = new T.Group(); g.position.set(19.95,0,-7.60); g.rotation.y = 0.15; world.add(g);
    const m = ctx.B(1.00,0.15,1.95, M(0x9d9682,.98), 0,0.075,0, 0, g); m.receiveShadow = true;
    inst(ctx,new T.BoxGeometry(0.96,0.006,0.11), M(0x8a8578,.98), 8,(i,d)=>{ d.position.set(0,0.152,-0.85+i*0.24); },false,g);
    ctx.B(0.98,0.075,0.90, M(0x5c5750,.98), 0.02,0.185,0.42, 0.05, g);       // the blanket, kicked down
    ctx.B(0.42,0.09,0.28, M(0xb9b2a2,.96), -0.16,0.195,-0.74, -0.2, g);      // the pillow
    ctx.B(0.13,0.026,0.19, M(0x8f887a,.94), 0.34,0.165,-0.55, 0.5, g, false);// a paperback, face down
    ctx.B(0.09,0.055,0.09, p.CHAR, 0.42,0.028,-1.10, 0.3, g, false);         // a radio on the floor
  }

  /* --- the bundles: the case is the furniture --- */
  {
    [[20.30,-5.40,22],[19.65,-6.35,15]].forEach(q=>{
      inst(ctx,new T.BoxGeometry(0.26,0.014,0.34), p.MANILA, q[2],(i,d)=>{
        d.position.set(q[0]+Math.sin(i*1.7)*0.018, 0.008+i*0.016, q[1]+Math.cos(i*2.1)*0.016);
        d.rotation.y = (i%7-3)*0.028;
      });
      inst(ctx,new T.BoxGeometry(0.28,0.004,0.016), M(0xb98a7c,.9), 3,(i,d)=>{
        d.position.set(q[0], 0.06+i*0.10, q[1]);
      });
    });
    const lab = tex(ctx,256,128,(gg,w,h)=>{
      gg.fillStyle='#8f887a'; gg.fillRect(0,0,w,h);
      gg.fillStyle='#cfc8b4'; gg.fillRect(8,8,w-16,h-16);
      gg.fillStyle='#332c22'; gg.font='700 22px Georgia, serif'; gg.textAlign='center';
      gg.fillText('BUNDLE 14 OF 40', w/2, 48);
      gg.font='16px Georgia, serif'; gg.fillStyle='#8a2f2a'; gg.fillText('EXTRADITION — PART B', w/2, 80);
      gg.fillStyle='#6d675c'; gg.font='13px Georgia, serif'; gg.fillText('do not remove from the room', w/2, 106);
    });
    inst(ctx,new T.BoxGeometry(0.52,0.34,0.40), M(0x6e6152,.95), 4,(i,d)=>{
      d.position.set(20.55+(i>1?0.10:0), 0.17+(i%2)*0.35, -4.75-(i>1?0.46:0)); d.rotation.y = (i-1.5)*0.12;
    });
    pic(ctx,lab, 20.55, 0.52, -4.54, 0.06, 0.40, 0.20);
  }

  /* --- taped under the desk: the thing the bundles are about --- */
  {
    b0(ctx,0.020,0.008,0.052, M(0x9a9aa2,.4,.6), 21.40,0.699,-9.25, 0.2);
    b0(ctx,0.050,0.002,0.030, M(0xcfc8b4,.96), 21.39,0.7035,-9.235, 0.2);
    b0(ctx,0.030,0.002,0.060, M(0xcfc8b4,.96), 21.41,0.7035,-9.265, 0.2);
  }

  /* --- the window: barred, high, and the only daylight in the room --- */
  {
    const g = new T.Group(); g.position.set(22.00,0,-12.30); world.add(g);
    ctx.B(1.05,0.85,0.10, M(0x1a1a1d,.9), 0,1.62,-0.03, 0, g);               // the reveal
    const pane = new T.Mesh(new T.PlaneGeometry(0.86,0.66), glowMat(ctx,0xd8d2c2,0xbfb49a,0.85));
    pane.position.set(0,1.62,0.028); g.add(pane);
    ctx.SCREENS.push({ m:pane.material, base:0.85 });
    inst(ctx,new T.BoxGeometry(0.030,0.86,0.030), p.STEEL, 5,(i,d)=>{ d.position.set(-0.36+i*0.18, 1.62, 0.055); },false,g);
    inst(ctx,new T.BoxGeometry(1.00,0.030,0.030), p.STEEL, 2,(i,d)=>{ d.position.set(0, 1.28+i*0.68, 0.055); },false,g);
    ctx.B(1.14,0.05,0.16, M(0x9d9682,.9), 0,1.16,0.06, 0, g, false);         // the sill
    /* the shaft, and the piece of floor it lands on */
    const shaft = new T.Mesh(new T.BoxGeometry(0.86,0.05,2.60), p.SHAFT);
    shaft.position.set(22.00,0.90,-11.02); shaft.rotation.x = -0.52; world.add(shaft);
    decal(ctx, new T.PlaneGeometry(0.94,1.30), p.SHAFT, 22.00, -9.90, 0.020);
  }

  /* --- the sun lamp: the embassy fix for never going outside --- */
  {
    const g = new T.Group(); g.position.set(24.20,0,-8.20); g.rotation.y = -0.5; world.add(g);
    inst(ctx,new T.BoxGeometry(0.030,0.92,0.030), p.CHAR, 3,(i,d)=>{
      const a = i/3*Math.PI*2; d.position.set(Math.cos(a)*0.19,0.46,Math.sin(a)*0.19);
      d.rotation.z = -Math.cos(a)*0.30; d.rotation.x = Math.sin(a)*0.30;
    },false,g);
    ctx.Cyl(0.026,0.72, p.CHAR, 0,1.24,0, g, 8);
    ctx.B(0.52,0.40,0.10, M(0xd6d0c2,.86), 0,1.56,0, 0.2, g);
    const face = new T.Mesh(new T.PlaneGeometry(0.44,0.32), glowMat(ctx,0xffe8c8,0xffd9a0,1.5));
    face.position.set(Math.sin(0.2)*0.055,1.56,Math.cos(0.2)*0.055); face.rotation.y = 0.2; g.add(face);
    ctx.SCREENS.push({ m:face.material, base:1.5 });
    ctx.B(0.10,0.06,0.05, p.CHAR, 0.20,1.34,0.03, 0.2, g, false);            // the timer that keeps running out
  }

  /* --- the map, on an easel, pinned and strung --- */
  {
    const e = easel(ctx, 19.55, -4.05, -1.00, 1.55);
    ctx.B(0.86,0.62,0.035, M(0x5a4a38,.95), 0,0.88,0.135, 0, e);
    const mp = tex(ctx,384,256,(gg,w,h)=>{
      gg.fillStyle='#1a1c1a'; gg.fillRect(0,0,w,h);
      gg.fillStyle='#4a5a4a';
      const blobs=[[40,60,90,60],[150,40,80,50],[210,110,120,70],[60,150,70,60],[300,60,60,90],[280,170,70,50]];
      blobs.forEach(b=>{ gg.beginPath(); gg.ellipse(b[0],b[1],b[2]/2,b[3]/2,0.3,0,6.3); gg.fill(); });
      gg.strokeStyle='rgba(140,132,110,0.35)'; gg.lineWidth=1;
      for(let x=0;x<w;x+=32){ gg.beginPath(); gg.moveTo(x,0); gg.lineTo(x,h); gg.stroke(); }
      for(let y=0;y<h;y+=32){ gg.beginPath(); gg.moveTo(0,y); gg.lineTo(w,y); gg.stroke(); }
      gg.strokeStyle='#8a2f2a'; gg.lineWidth=2;
      const pins=[[70,70],[168,58],[240,120],[86,168],[318,84],[296,182]];
      for(let i=1;i<pins.length;i++){ gg.beginPath(); gg.moveTo(pins[i-1][0],pins[i-1][1]); gg.lineTo(pins[i][0],pins[i][1]); gg.stroke(); }
      gg.fillStyle='#d98a4a'; pins.forEach(q=>{ gg.beginPath(); gg.arc(q[0],q[1],5,0,6.3); gg.fill(); });
    });
    const mf = new T.Mesh(new T.PlaneGeometry(0.78,0.52), new T.MeshStandardMaterial({ map:mp, roughness:.8 }));
    mf.position.set(0,0.88,0.156); e.add(mf);
    inst(ctx,new T.SphereGeometry(0.010,7,5), M(0xd98a4a,.6), 8,(i,d)=>{     // real pin heads over the printed ones
      d.position.set(-0.30+((i*11)%7)*0.10, 0.70+((i*5)%5)*0.09, 0.166);
    },false,e);
  }

  /* --- a crate of drives, still spinning --- */
  {
    ctx.B(0.56,0.34,0.42, M(0x8a7a5c,.9), 24.30,0.17,-5.60, -0.25, world);
    inst(ctx,new T.BoxGeometry(0.11,0.022,0.15), p.SLATE, 7,(i,d)=>{
      d.position.set(24.30+((i%2)-0.5)*0.16, 0.36+i*0.024, -5.60+((i%3)-1)*0.07); d.rotation.y = -0.25+(i%4-1.5)*0.10;
    });
    const led = b0(ctx,0.014,0.014,0.006, glowMat(ctx,0x222222,0xd9a05e,1.4), 24.36,0.52,-5.52, -0.25);
    ctx.BLINKS.push({ m:led.material, ph:4.4 });
  }
}

/* ============================================================================
   BEN — room [-23,-12.5 → -15,12.5] · the only room with a door
   Story: a study, not an office — a fire, a blackboard with the chain on it,
   and a table where three decisions are laid out and two will be told no.
   82 draw calls · bbox x -22.93..-15.85, z -11.80..11.24
   (The middle of the room, z -5..5, is left exactly as the host furnished it.)
   ========================================================================= */
export function dressBen(ctx){
  const T = ctx.THREE, p = pal(ctx), { B, Cyl, M, world } = ctx;

  /* --- the fire, on the one wall tall enough to carry a chimney breast --- */
  {
    const g = new T.Group(); g.position.set(-22.62,0,7.80); g.rotation.y = Math.PI/2; world.add(g);
    ctx.B(1.86,0.06,0.86, M(0x3c3830,.94), 0,0.03,0.30, 0, g, false);        // the hearth slab
    ctx.B(0.34,1.26,0.34, M(0x4a443a,.92), -0.76,0.63,0, 0, g);              // jambs
    ctx.B(0.34,1.26,0.34, M(0x4a443a,.92),  0.76,0.63,0, 0, g);
    ctx.B(1.86,0.26,0.34, M(0x4a443a,.92), 0,1.39,0, 0, g);                  // lintel
    ctx.B(2.06,0.09,0.46, ctx.WOODD, 0,1.56,0.02, 0, g);                     // the mantel
    ctx.B(1.20,1.20,0.24, M(0x17161a,.98), 0,0.60,-0.14, 0, g);              // the firebox
    ctx.B(1.34,0.55,0.10, M(0x2b2b30,.85), 0,2.03,-0.06, 0, g);              // the breast above it
    /* the fire itself, on the candle loop so it moves */
    inst(ctx,new T.CylinderGeometry(0.05,0.05,0.52,7), M(0x2a231d,.98), 5,(i,d)=>{
      d.position.set(-0.20+i*0.10, 0.14+((i%2)*0.07), -0.08+((i%3)-1)*0.05);
      d.rotation.z = Math.PI/2 - 0.3 + i*0.15; d.rotation.y = (i-2)*0.28;
    },false,g);
    const emb = new T.Mesh(new T.SphereGeometry(0.30,14,9,0,6.3,1.1,1.0), glowMat(ctx,0xff9a4a,0xff8a3a,1.9));
    emb.scale.set(1.5,1,0.7); emb.position.set(0,0.17,-0.08); g.add(emb);
    ctx.CANDLES.push({ m:emb.material, base:1.9, ph:0.4 });
    const flame = new T.Mesh(new T.ConeGeometry(0.16,0.42,10), glowMat(ctx,0xffd9a0,0xffb055,2.2));
    flame.position.set(-0.02,0.36,-0.08); flame.scale.set(1.4,1,0.6); g.add(flame);
    ctx.CANDLES.push({ m:flame.material, base:2.2, ph:2.6 });
    /* what lives on a mantel */
    ctx.B(0.16,0.20,0.11, ctx.WOODD, -0.60,1.71,0.02, 0.1, g, false);        // a carriage clock
    ctx.Cyl(0.055,0.014, p.GOLD, -0.60,1.71,0.078, g, 14).rotation.x = Math.PI/2;
    ctx.Cyl(0.026,0.20, p.BRASS, 0.66,1.71,0.02, g, 8).castShadow = false;   // a candlestick
    ctx.candle(-22.60, 1.81, 7.14, 0.020);
    [[-0.24,0.10],[0.16,-0.08]].forEach(q=>{                                 // two photographs, propped
      const fr = ctx.B(0.20,0.15,0.02, M(0x3a2f24,.78,.1), q[0],1.685,0.06, q[1], g, false);
      fr.rotation.x = 0.14;
      ctx.B(0.16,0.11,0.008, M(0x8f887a,.9), q[0],1.687,0.072, q[1], g, false).rotation.x = 0.14;
    });
    B(2.10,0.03,1.50, M(0x2a2119,.98), -21.30,0.028,7.80, 0, world, false);  // the rug in front of it
  }
  /* the chair he actually reads in, and what ends up beside it */
  {
    const g = new T.Group(); g.position.set(-21.05,0,7.90); g.rotation.y = -Math.PI/2; world.add(g);
    const hide = M(0x4a3a2c,.94);
    ctx.B(0.78,0.16,0.72, hide, 0,0.40,0, 0, g);                             // seat
    ctx.B(0.78,0.74,0.18, hide, 0,0.75,-0.30, 0, g);                         // back
    ctx.B(0.16,0.46,0.72, hide, -0.34,0.60,0.02, 0, g);                      // arms
    ctx.B(0.16,0.46,0.72, hide,  0.34,0.60,0.02, 0, g);
    ctx.B(0.74,0.32,0.68, M(0x3a2d22,.94), 0,0.16,0, 0, g);                  // the base
    ctx.B(0.52,0.30,0.42, hide, 0.02,0.15,0.72, 0, g);                       // footstool
    ctx.B(0.44,0.60,0.44, ctx.WOODD, -21.05,0.30,9.05, 0, world);            // side table
    ctx.B(0.50,0.04,0.50, ctx.WOODL, -21.05,0.62,9.05, 0, world);
    b0(ctx,0.16,0.05,0.22, M(0x4f5a4e,.9), -21.05,0.665,9.05, 0.3);          // one book, face down
    ctx.Cyl(0.032,0.10, M(0x8a6f42,.3,.7), -20.92,0.69,8.90, world, 10).castShadow = false;
  }

  /* --- the blackboard: the whole company, in chalk, where only he can see it --- */
  {
    const chalk = tex(ctx,1024,608,(g,w,h)=>{
      g.fillStyle='#1e2320'; g.fillRect(0,0,w,h);
      g.fillStyle='rgba(207,200,180,0.05)';
      for(let i=0;i<260;i++) g.fillRect((i*97)%w, (i*61)%h, 40+((i*7)%90), 2);
      const box=(x,y,ww,hh,txt,sub)=>{
        g.strokeStyle='#cfc8b4'; g.lineWidth=3; g.strokeRect(x,y,ww,hh);
        g.fillStyle='#cfc8b4'; g.textAlign='center';
        g.font='600 27px Futura, Helvetica, sans-serif'; g.fillText(txt, x+ww/2, y+hh/2+ (sub?-2:10));
        if(sub){ g.font='16px Futura, Helvetica, sans-serif'; g.fillStyle='#8f887a'; g.fillText(sub, x+ww/2, y+hh/2+24); }
      };
      const arrow=(x1,y1,x2,y2)=>{ g.strokeStyle='#d98a4a'; g.lineWidth=4;
        g.beginPath(); g.moveTo(x1,y1); g.lineTo(x2,y2); g.stroke();
        const a=Math.atan2(y2-y1,x2-x1); g.beginPath();
        g.moveTo(x2,y2); g.lineTo(x2-Math.cos(a-0.4)*16, y2-Math.sin(a-0.4)*16);
        g.lineTo(x2-Math.cos(a+0.4)*16, y2-Math.sin(a+0.4)*16); g.closePath(); g.fillStyle='#d98a4a'; g.fill(); };
      g.fillStyle='#cfc8b4'; g.font='italic 30px Georgia, serif'; g.textAlign='left';
      g.fillText('the whole org — one door', 44, 60);
      box(44,110,190,86,'EMAIL','arrives');
      box(272,110,190,86,'JANNA','lands here');
      box(500,110,190,86,'THE SEAT','does the work');
      box(728,110,190,86,'ALEXANDER','carries it');
      box(390,330,230,110,'BEN','the only YES');
      box(700,352,230,86,'IT GOES OUT', null);
      arrow(238,153,268,153); arrow(466,153,496,153); arrow(694,153,724,153);
      arrow(822,200,540,326); arrow(624,392,696,394);
      g.strokeStyle='#8a2f2a'; g.lineWidth=5;
      g.beginPath(); g.moveTo(300,470); g.lineTo(360,530); g.moveTo(360,470); g.lineTo(300,530); g.stroke();
      g.fillStyle='#8f887a'; g.font='23px Futura, Helvetica, sans-serif'; g.textAlign='left';
      g.fillText('nothing skips a box. nothing leaves without the last one.', 390, 512);
      g.fillText('fewer decisions. better ones.', 390, 552);
    });
    const g2 = new T.Group(); g2.position.set(-22.72,1.58,-8.60); g2.rotation.y = Math.PI/2; world.add(g2);
    ctx.B(2.34,1.46,0.06, M(0x2f2921,.9), 0,0,0, 0, g2);
    const f = new T.Mesh(new T.PlaneGeometry(2.22,1.32), new T.MeshStandardMaterial({ map:chalk, roughness:.92 }));
    f.position.z = 0.036; g2.add(f);
    ctx.B(2.42,0.06,0.14, ctx.WOODD, 0,-0.76,0.05, 0, g2, false);            // the chalk ledge
    inst(ctx,new T.CylinderGeometry(0.010,0.010,0.07,6), M(0xe4e0d2,.95), 3,(i,d)=>{
      d.position.set(-0.42+i*0.14,-0.71,0.08); d.rotation.z = Math.PI/2; d.rotation.y = i*0.4;
    },false,g2);
    inst(ctx,new T.BoxGeometry(0.10,0.10,0.06), M(0x5c5750,.98), 1,(i,d)=>{ d.position.set(0.62,-0.70,0.08); },false,g2);
    inst(ctx,new T.BoxGeometry(0.09,0.16,0.09), p.CHAR, 2,(i,d)=>{           // the brackets that hold it up
      d.position.set(-0.90+i*1.80, 0.80, -0.03);
    },false,g2);
  }

  /* --- the decision table: three folders, and only one of them survives --- */
  {
    const g = new T.Group(); g.position.set(-19.00,0,-8.40); world.add(g);
    ctx.B(2.60,0.07,1.00, ctx.WOODD, 0,0.76,0, 0, g);
    [[-1.16,-0.40],[1.16,-0.40],[-1.16,0.40],[1.16,0.40]].forEach(q=>ctx.B(0.10,0.74,0.10, ctx.WOODD, q[0],0.37,q[1], 0, g));
    ctx.B(2.30,0.06,0.08, ctx.WOODD, 0,0.20,0, 0, g, false);                 // the stretcher
    const tab = tex(ctx,256,64,(gg,w,h)=>{ gg.fillStyle='#b8a97e'; gg.fillRect(0,0,w,h);
      gg.fillStyle='#332c22'; gg.font='600 24px Futura, sans-serif'; gg.textAlign='center';
      gg.fillText('FOR HIS YES', w/2, 42); });
    [-0.82,0,0.82].forEach((x,i)=>{
      ctx.B(0.34,0.030,0.44, p.MANILA, x,0.812,0, (i-1)*0.05, g, false);     // the folder
      inst(ctx,new T.BoxGeometry(0.30,0.006,0.40), ctx.PAPER, 4,(i2,d)=>{
        d.position.set(x, 0.800+i2*0.005, 0.01); d.rotation.y = (i-1)*0.05 + (i2-1.5)*0.02;
      },false,g);
      pic(ctx,tab, -19.00+x, 0.829, -8.62, 0, 0.26, 0.065, { rx:-Math.PI/2 });
      /* the two cards that decide it */
      ctx.B(0.14,0.004,0.09, M(0x4f5a4e,.9), x-0.09,0.830,0.34, (i-1)*0.05, g, false);
      ctx.B(0.14,0.004,0.09, M(0x8a2f2a,.9), x+0.09,0.830,0.34, (i-1)*0.05, g, false);
    });
    ctx.Cyl(0.008,0.14, p.JET, 0.34,0.822,-0.34, g, 8).rotation.z = Math.PI/2;   // one pen, capped
    ctx.Cyl(0.010,0.04, p.GOLD, 0.42,0.822,-0.34, g, 8).rotation.z = Math.PI/2;
    ctx.chair(-19.00,-9.40, 0);
    /* a reading lamp, because the north end has no window */
    ctx.Cyl(0.10,0.02, p.BRASS, -1.02,0.805,-0.30, g, 12).castShadow = false;
    const arm = ctx.Cyl(0.014,0.34, p.BRASS, -1.02,0.96,-0.30, g, 6); arm.rotation.z = 0.35; arm.castShadow = false;
    const sh = new T.Mesh(new T.CylinderGeometry(0.05,0.10,0.09,12,1,true), p.BRASS);
    sh.position.set(-1.14,1.12,-0.30); sh.rotation.z = 0.35; g.add(sh);
    const bulb = new T.Mesh(new T.SphereGeometry(0.030,10,8), glowMat(ctx,0xffd9a0,0xffc37a,1.5));
    bulb.position.set(-20.13,1.08,-8.70); world.add(bulb);
  }

  /* --- the globe: he still thinks about where this goes --- */
  {
    const gt = tex(ctx,512,256,(gg,w,h)=>{
      gg.fillStyle='#2b3a38'; gg.fillRect(0,0,w,h);
      gg.fillStyle='#8a7a52';
      const land=[[40,70,90,70],[120,60,60,44],[150,120,50,80],[210,60,80,50],[250,120,44,60],
                  [300,70,110,60],[330,140,80,66],[420,150,60,50],[470,80,50,40],[80,190,70,40]];
      land.forEach(b=>{ gg.beginPath(); gg.ellipse(b[0],b[1],b[2]/2,b[3]/2,(b[0]%7)*0.2,0,6.3); gg.fill(); });
      gg.strokeStyle='rgba(207,200,180,0.20)'; gg.lineWidth=1;
      for(let y=0;y<h;y+=32){ gg.beginPath(); gg.moveTo(0,y); gg.lineTo(w,y); gg.stroke(); }
      for(let x=0;x<w;x+=32){ gg.beginPath(); gg.moveTo(x,0); gg.lineTo(x,h); gg.stroke(); }
      gg.fillStyle='#d98a4a'; gg.beginPath(); gg.arc(392,176,7,0,6.3); gg.fill();
    });
    const g = new T.Group(); g.position.set(-16.60,0,-9.40); g.rotation.y = -0.4; world.add(g);
    inst(ctx,new T.BoxGeometry(0.035,0.70,0.035), ctx.WOODD, 3,(i,d)=>{
      const a = i/3*Math.PI*2; d.position.set(Math.cos(a)*0.17,0.35,Math.sin(a)*0.17);
      d.rotation.z = -Math.cos(a)*0.26; d.rotation.x = Math.sin(a)*0.26;
    },false,g);
    const ring = new T.Mesh(new T.TorusGeometry(0.20,0.012,6,20), ctx.WOODD);
    ring.rotation.x = Math.PI/2; ring.position.y = 0.66; g.add(ring);
    const ball = new T.Mesh(new T.SphereGeometry(0.21,26,18), new T.MeshStandardMaterial({ map:gt, roughness:.86 }));
    ball.position.y = 0.90; ball.rotation.z = 0.41; ball.castShadow = true; g.add(ball);
    const mer = new T.Mesh(new T.TorusGeometry(0.235,0.010,6,26, 4.4), p.BRASS);
    mer.rotation.y = Math.PI/2; mer.rotation.z = 0.41; mer.position.y = 0.90; g.add(mer);
  }

  /* --- the safe: the one thing on this floor that is actually locked --- */
  {
    const g = new T.Group(); g.position.set(-16.30,0,-11.40); g.rotation.y = -0.20; world.add(g);
    ctx.B(0.78,0.92,0.66, M(0x2b2b30,.72,.30), 0,0.46,0, 0, g);
    ctx.B(0.66,0.80,0.05, M(0x35353c,.66,.35), 0,0.48,0.335, 0, g);
    ctx.Cyl(0.11,0.05, M(0x9a9aa2,.35,.72), -0.08,0.52,0.365, g, 20).rotation.x = Math.PI/2;
    const dl = tex(ctx,128,128,(gg)=>{
      gg.fillStyle='#1b1b1e'; gg.beginPath(); gg.arc(64,64,60,0,6.3); gg.fill();
      gg.strokeStyle='#cfc8b4'; gg.lineWidth=2;
      for(let i=0;i<40;i++){ const a=i/40*6.283; gg.beginPath();
        gg.moveTo(64+Math.cos(a)*54,64+Math.sin(a)*54); gg.lineTo(64+Math.cos(a)*(i%5?47:41),64+Math.sin(a)*(i%5?47:41)); gg.stroke(); }
      gg.fillStyle='#cfc8b4'; gg.font='700 15px Menlo, monospace'; gg.textAlign='center';
      for(let i=0;i<8;i++){ const a=-1.57+i*0.785; gg.fillText(String(i*10), 64+Math.cos(a)*32, 64+Math.sin(a)*32+5); }
      gg.fillStyle='#8a2f2a'; gg.fillRect(62,4,4,14);
    });
    pic(ctx,dl, -16.30-Math.sin(-0.20)*0.392-Math.cos(-0.20)*0.08, 0.52,
                -11.40+Math.cos(-0.20)*0.392-Math.sin(-0.20)*0.08, -0.20, 0.20, 0.20);
    inst(ctx,new T.BoxGeometry(0.020,0.020,0.16), M(0x9a9aa2,.35,.72), 3,(i,d)=>{  // the handle spokes
      d.position.set(0.20,0.48,0.36); d.rotation.z = i*2.09;
    },false,g);
    ctx.B(0.16,0.05,0.012, p.GOLD, 0,0.82,0.365, 0, g, false);
  }

  /* --- the clock, and the books he has not read yet --- */
  {
    const g = new T.Group(); g.position.set(-22.55,0,10.90); g.rotation.y = Math.PI/2; world.add(g);
    ctx.B(0.54,1.90,0.34, ctx.WOODD, 0,0.95,0, 0, g);
    ctx.B(0.62,0.14,0.42, ctx.WOODL, 0,1.97,0, 0, g);                        // hood
    ctx.B(0.68,0.06,0.46, ctx.WOODD, 0,0.03,0, 0, g, false);                 // plinth
    ctx.B(0.34,0.86,0.03, p.GLASS, 0,1.05,0.175, 0, g, false);               // the pendulum case
    const pend = ctx.Cyl(0.008,0.66, p.BRASS, 0,1.16,0.10, g, 6); pend.castShadow = false;
    ctx.Cyl(0.075,0.014, p.BRASS, 0,0.83,0.10, g, 16).rotation.x = Math.PI/2;
    const cf = tex(ctx,256,256,(gg)=>{
      gg.fillStyle='#e0dac6'; gg.beginPath(); gg.arc(128,128,118,0,6.3); gg.fill();
      gg.strokeStyle='#8a6f42'; gg.lineWidth=5; gg.beginPath(); gg.arc(128,128,112,0,6.3); gg.stroke();
      gg.fillStyle='#2f2921'; gg.font='700 22px Georgia, serif'; gg.textAlign='center';
      const R=['XII','I','II','III','IIII','V','VI','VII','VIII','IX','X','XI'];
      for(let i=0;i<12;i++){ const a=-1.5708+i*0.5236; gg.fillText(R[i], 128+Math.cos(a)*88, 128+Math.sin(a)*88+8); }
      gg.strokeStyle='#2f2921'; gg.lineWidth=8; gg.lineCap='round';
      gg.beginPath(); gg.moveTo(128,128); gg.lineTo(128+Math.cos(-1.0)*54,128+Math.sin(-1.0)*54); gg.stroke();
      gg.lineWidth=5; gg.beginPath(); gg.moveTo(128,128); gg.lineTo(128+Math.cos(2.1)*84,128+Math.sin(2.1)*84); gg.stroke();
      gg.fillStyle='#8a6f42'; gg.beginPath(); gg.arc(128,128,9,0,6.3); gg.fill();
      gg.font='italic 17px Georgia, serif'; gg.fillStyle='#6d675c'; gg.fillText('tempus fugit',128,196);
    });
    pic(ctx,cf, -22.375, 1.66, 10.90, Math.PI/2, 0.40, 0.40);
    inst(ctx,new T.BoxGeometry(0.16,0.045,0.24), M(0x77624a,.9), 11,(i,d)=>{ // the unread pile
      d.position.set(-20.40+((i%2)*0.02), 0.024+i*0.048, 10.60+((i%3)*0.02)); d.rotation.y = (i%5-2)*0.06;
      return [0x6e4a3a,0x4f5a4e,0x8a7a5c,0x5a5a46,0x6b5566][i%5];
    },true);
    b0(ctx,0.17,0.045,0.25, M(0x4a4f5a,.9), -20.72,0.024,10.86, 0.5);
  }
}

/* ============================================================================
   GRANTS POD — room [25,-12.5 → 30,12.5] · the mouth opens across x=25, z -2..2
   Story: everything is prepared, indexed and dated, and the button is under glass.
   64 draw calls · bbox x 25.29..29.67, z -11.03..9.87
   ========================================================================= */
export function dressGrants(ctx){
  const T = ctx.THREE, p = pal(ctx), { M, world } = ctx;

  /* --- the lodgement console: the button nobody has ever pressed --- */
  {
    const g = new T.Group(); g.position.set(27.60,0,6.20); g.rotation.y = Math.PI; world.add(g);
    ctx.B(1.10,0.88,0.62, M(0x35353c,.72,.20), 0,0.44,0, 0, g);
    const top = ctx.B(1.10,0.06,0.52, M(0x2b2b30,.66,.28), 0,0.90,0.02, 0, g); top.rotation.x = -0.30;
    ctx.B(1.14,0.30,0.06, M(0x2b2b30,.66,.28), 0,1.06,-0.22, 0, g);
    const fasc = tex(ctx,512,192,(gg,w,h)=>{
      gg.fillStyle='#14161a'; gg.fillRect(0,0,w,h);
      gg.strokeStyle='#5c5a4e'; gg.lineWidth=3; gg.strokeRect(10,10,w-20,h-20);
      gg.fillStyle='#cfc8b4'; gg.font='700 34px Futura, Helvetica, sans-serif'; gg.textAlign='left';
      gg.fillText('LODGEMENT', 32, 62);
      gg.font='19px Menlo, monospace'; gg.fillStyle='#8f887a';
      gg.fillText('FY25  ·  prepared 100%  ·  evidence 100%', 32, 100);
      gg.fillText('status: PARKED — awaiting his YES', 32, 132);
      gg.fillStyle='#d98a4a'; gg.fillRect(32,150,196,4);
      gg.fillStyle='#8a2f2a'; gg.font='700 22px Futura, sans-serif'; gg.textAlign='right';
      gg.fillText('LODGED: 0', w-32, 62);
    });
    const ff = new T.Mesh(new T.PlaneGeometry(0.98,0.37), new T.MeshStandardMaterial({ map:fasc, roughness:.6,
      emissive:0xffffff, emissiveMap:fasc, emissiveIntensity:.38 }));
    ff.position.set(0,1.06,-0.185); g.add(ff);
    ctx.SCREENS.push({ m:ff.material, base:.38 });
    /* the button, under a cover that has never been lifted */
    ctx.Cyl(0.085,0.03, M(0x1c1c1f,.6), 0.30,0.955,0.10, g, 18).castShadow = false;
    const btn = ctx.Cyl(0.062,0.045, p.OX, 0.30,0.982,0.10, g, 18); btn.castShadow = false;
    const dome = new T.Mesh(new T.SphereGeometry(0.062,14,8,0,6.3,0,1.2), p.OX);
    dome.position.set(0.30,0.998,0.10); g.add(dome);
    const cov = new T.Mesh(new T.SphereGeometry(0.10,16,10,0,6.3,0,1.35), p.GLASS);
    cov.position.set(0.30,0.955,0.10); g.add(cov);
    ctx.B(0.05,0.03,0.09, p.STEEL, 0.30,0.965,0.005, 0, g, false);           // the hinge
    ctx.B(0.04,0.05,0.02, p.BRASS, 0.30,0.975,0.20, 0, g, false);            // the hasp, closed
    /* the key switch, turned off, and a standby lamp nobody watches */
    ctx.Cyl(0.038,0.022, p.STEEL, -0.32,0.952,0.10, g, 14).castShadow = false;
    const key = ctx.B(0.012,0.010,0.055, p.BRASS, -0.32,0.968,0.12, 0.9, g, false);
    const lamp = ctx.B(0.030,0.030,0.010, glowMat(ctx,0x222222,0xd9a05e,1.2), -0.12,0.958,0.12, 0, g, false);
    ctx.BLINKS.push({ m:lamp.material, ph:5.2 });
    const lab = tex(ctx,256,64,(gg,w,h)=>{ gg.fillStyle='#14161a'; gg.fillRect(0,0,w,h);
      gg.fillStyle='#8a2f2a'; gg.font='700 30px Futura, sans-serif'; gg.textAlign='center'; gg.fillText('LODGE', w/2, 44); });
    pic(ctx,lab, 27.30, 0.885, 5.90, Math.PI, 0.24, 0.06, { rx:-0.30 });
  }

  /* --- FY25, parked: nine boxes, wrapped, strapped and tagged --- */
  {
    const g = new T.Group(); g.position.set(27.40,0,-8.40); g.rotation.y = 0.12; world.add(g);
    inst(ctx,new T.BoxGeometry(1.32,0.05,0.16), M(0x8a7a5c,.94), 3,(i,d)=>{ d.position.set(0,0.075,-0.38+i*0.38); },false,g);
    inst(ctx,new T.BoxGeometry(1.32,0.06,0.10), M(0x77624a,.94), 2,(i,d)=>{ d.position.set(0,0.03,-0.28+i*0.56); },false,g);
    inst(ctx,new T.BoxGeometry(0.40,0.30,0.34), M(0x6e6152,.95), 9,(i,d)=>{
      d.position.set(-0.44+(i%3)*0.44, 0.255+((i/3)|0)*0.31, ((i*5)%3-1)*0.06);
      d.rotation.y = (i%4-1.5)*0.04;
    },false,g);
    const wrap = ctx.B(1.36,0.96,0.60,
      new T.MeshStandardMaterial({ color:0xc6c6c0, roughness:.24, transparent:true, opacity:.20, depthWrite:false }),
      0,0.58,0, 0, g, false);
    wrap.receiveShadow = false;
    ctx.B(1.42,0.030,0.045, M(0xd98a4a,.7), 0,0.60,0.30, 0, g, false);       // the ratchet strap
    ctx.B(1.42,0.030,0.045, M(0xd98a4a,.7), 0,0.60,-0.30, 0, g, false);
    ctx.B(0.045,0.96,0.030, M(0xd98a4a,.7), 0.10,0.58,0.305, 0, g, false);
    ctx.B(0.11,0.09,0.06, p.STEEL, 0.10,0.62,0.335, 0, g, false);            // the buckle
    const tag = tex(ctx,256,160,(gg,w,h)=>{
      gg.fillStyle='#c9c2ac'; gg.fillRect(0,0,w,h);
      gg.strokeStyle='#8a2f2a'; gg.lineWidth=5; gg.strokeRect(10,10,w-20,h-20);
      gg.fillStyle='#8a2f2a'; gg.font='700 44px Futura, Helvetica, sans-serif'; gg.textAlign='center';
      gg.fillText('FY25', w/2, 62);
      gg.font='700 22px Futura, sans-serif'; gg.fillText('PARKED', w/2, 96);
      gg.fillStyle='#5c5548'; gg.font='15px Menlo, monospace'; gg.fillText('do not lodge · not signed', w/2, 130);
    });
    ctx.Cyl(0.004,0.20, p.RUST, 0.44,1.14,0.30, g, 5).castShadow = false;
    const tg = new T.Mesh(new T.PlaneGeometry(0.28,0.175), new T.MeshStandardMaterial({ map:tag, roughness:.8, side:T.DoubleSide }));
    tg.position.set(0.44,0.94,0.30); tg.rotation.y = 0.12; g.add(tg);
  }

  /* --- the eligibility board, on an easel, ticks and one circle --- */
  {
    const e = easel(ctx, 25.75, -6.20, Math.PI/2 - 0.20, 1.62);
    ctx.B(1.02,0.74,0.03, M(0xd8d2c2,.92), 0,0.92,0.14, 0, e);
    const bd = tex(ctx,512,384,(gg,w,h)=>{
      gg.fillStyle='#e0dbcc'; gg.fillRect(0,0,w,h);
      gg.fillStyle='#332c22'; gg.font='700 30px Futura, Helvetica, sans-serif';
      gg.fillText('ELIGIBLE?', 28, 50);
      gg.fillStyle='#8f887a'; gg.fillRect(28,64,w-56,3);
      const rows=[['core R&D activity','y'],['new knowledge','y'],['systematic progression','y'],
                  ['expenditure evidenced','y'],['registered in time','n'],['AusIndustry number','n'],['signed by him','q']];
      rows.forEach((r,i)=>{
        const y = 108+i*38;
        gg.fillStyle='#4a443a'; gg.font='22px Futura, Helvetica, sans-serif'; gg.fillText(r[0], 78, y);
        if(r[1]==='y'){ gg.strokeStyle='#4a6a5e'; gg.lineWidth=6; gg.beginPath();
          gg.moveTo(36,y-8); gg.lineTo(46,y+2); gg.lineTo(64,y-20); gg.stroke(); }
        else if(r[1]==='n'){ gg.strokeStyle='#8a2f2a'; gg.lineWidth=6; gg.beginPath();
          gg.moveTo(36,y-20); gg.lineTo(62,y+2); gg.moveTo(62,y-20); gg.lineTo(36,y+2); gg.stroke(); }
        else { gg.strokeStyle='#d98a4a'; gg.lineWidth=5; gg.beginPath(); gg.ellipse(w/2-30,y-8,236,26,0,0,6.3); gg.stroke(); }
      });
    });
    const bf = new T.Mesh(new T.PlaneGeometry(0.94,0.70), new T.MeshStandardMaterial({ map:bd, roughness:.82 }));
    bf.position.set(0,0.92,0.158); e.add(bf);
  }

  /* --- the years, in binders, on their own run --- */
  {
    const g = new T.Group(); g.position.set(29.50,0,4.80); g.rotation.y = -Math.PI/2; world.add(g);
    ctx.B(2.40,1.52,0.04, M(0x2f2921,.9), 0,0.76,-0.15, 0, g);
    ctx.B(0.05,1.52,0.32, ctx.WOODD, -1.19,0.76,0, 0, g);
    ctx.B(0.05,1.52,0.32, ctx.WOODD,  1.19,0.76,0, 0, g);
    [0.05,0.53,1.01,1.50].forEach(y=>ctx.B(2.40,0.045,0.32, ctx.WOODD, 0,y,0, 0, g));
    const sp = tex(ctx,768,384,(gg,w,h)=>{
      gg.fillStyle='#191712'; gg.fillRect(0,0,w,h);
      const cols=['#4a4f5a','#6e4a3a','#4f5a4e','#5a5a46','#6b5566','#77624a'];
      for(let r=0;r<3;r++) for(let i=0;i<16;i++){
        const x0=6+i*47, y0=r*128, ww=40;
        gg.fillStyle = cols[(i+r*2)%cols.length];
        gg.fillRect(x0,y0+6,ww,116);
        gg.fillStyle='rgba(232,226,208,0.85)'; gg.fillRect(x0+4,y0+26,ww-8,30);
        gg.fillStyle='#241f18'; gg.font='700 17px Futura, Helvetica, sans-serif'; gg.textAlign='center';
        gg.fillText('FY'+(19+((r*16+i)%8)), x0+ww/2, y0+47);
        gg.fillStyle='rgba(232,226,208,0.45)'; gg.fillRect(x0+4,y0+94,ww-8,4);
      }
    });
    const sf = new T.Mesh(new T.PlaneGeometry(2.32,1.36), new T.MeshStandardMaterial({ map:sp, roughness:.88 }));
    sf.position.set(0,0.77,0.06); g.add(sf);
    inst(ctx,new T.BoxGeometry(0.09,0.30,0.28), M(0x6e4a3a,.9), 4,(i,d)=>{   // four pulled forward
      d.position.set(-0.80+i*0.52, 0.23+((i%2)*0.48), 0.09); d.rotation.z = (i%2)?0.10:-0.06;
    },false,g);
  }

  /* --- the deadline that already passed, on its own easel --- */
  {
    const e = easel(ctx, 29.20, 8.80, -Math.PI/2 + 0.20, 1.50);
    ctx.B(0.94,0.68,0.03, M(0x2f2921,.9), 0,0.86,0.14, 0, e);
    const cal = tex(ctx,512,352,(gg,w,h)=>{
      gg.fillStyle='#e0dbcc'; gg.fillRect(0,0,w,h);
      gg.fillStyle='#1c1a17'; gg.fillRect(0,0,w,56);
      gg.fillStyle='#cfc8b4'; gg.font='700 28px Futura, Helvetica, sans-serif'; gg.textAlign='center';
      gg.fillText('REGISTRATION CLOSES', w/2, 38);
      gg.fillStyle='#8f887a'; gg.font='16px Futura, sans-serif';
      ['M','T','W','T','F','S','S'].forEach((d2,i)=>gg.fillText(d2, 48+i*68, 84));
      gg.font='20px Futura, sans-serif';
      for(let r=0;r<4;r++) for(let c=0;c<7;c++){
        const n = r*7+c+1; gg.fillStyle = '#4a443a';
        gg.fillText(String(n), 48+c*68, 122+r*54);
      }
      gg.strokeStyle='#8a2f2a'; gg.lineWidth=5; gg.beginPath(); gg.arc(48+2*68, 122+2*54-7, 26, 0, 6.3); gg.stroke();
      gg.fillStyle='#8a2f2a'; gg.font='700 20px Futura, sans-serif'; gg.fillText('PASSED', w/2, h-16);
    });
    const cf2 = new T.Mesh(new T.PlaneGeometry(0.86,0.60), new T.MeshStandardMaterial({ map:cal, roughness:.82 }));
    cf2.position.set(0,0.86,0.158); e.add(cf2);
  }

  /* --- the urn, because a shared table runs on it --- */
  {
    const g = new T.Group(); g.position.set(26.40,0,9.40); g.rotation.y = 0.35; world.add(g);
    ctx.B(1.10,0.72,0.52, ctx.WOODD, 0,0.36,0, 0, g);
    ctx.B(1.18,0.045,0.58, ctx.WOODL, 0,0.74,0, 0, g);
    ctx.Cyl(0.15,0.42, M(0x9a9aa2,.30,.75), -0.28,0.97,0, g, 16);
    ctx.Cyl(0.13,0.05, M(0x9a9aa2,.30,.75), -0.28,1.20,0, g, 16).castShadow = false;
    ctx.B(0.04,0.06,0.05, p.JET, -0.28,0.84,0.16, 0, g, false);              // the tap
    ctx.B(0.06,0.02,0.05, p.JET, -0.28,0.87,0.18, 0, g, false);
    inst(ctx,new T.CylinderGeometry(0.038,0.030,0.07,12), M(0xe4e0d2,.5), 6,(i,d)=>{
      d.position.set(0.02+(i%3)*0.11, 0.80, -0.12+((i/3)|0)*0.14);
    },false,g);
    ctx.B(0.20,0.09,0.14, M(0x8a7a5c,.9), 0.40,0.81,0.06, 0.3, g, false);    // the biscuit tin
  }

  /* --- the stamp that gets used instead of the button --- */
  {
    ctx.B(0.10,0.055,0.07, p.JET, 28.15,0.815,0.95, 0.4, world, false);
    ctx.Cyl(0.016,0.055, ctx.WOODD, 28.15,0.868,0.95, world, 8).castShadow = false;
    ctx.Cyl(0.028,0.018, ctx.WOODD, 28.15,0.902,0.95, world, 10).castShadow = false;
    ctx.B(0.14,0.026,0.10, M(0x8a2f2a,.8), 28.36,0.800,0.88, 0.4, world, false);
    const stamped = tex(ctx,256,128,(gg,w,h)=>{
      gg.fillStyle='#e4e0d2'; gg.fillRect(0,0,w,h);
      gg.fillStyle='#8f887a'; for(let i=0;i<7;i++) gg.fillRect(20,26+i*13,150+((i*29)%70),3);
      gg.save(); gg.translate(w/2,h/2); gg.rotate(-0.22);
      gg.strokeStyle='rgba(138,47,42,0.85)'; gg.lineWidth=4; gg.strokeRect(-92,-26,184,52);
      gg.fillStyle='rgba(138,47,42,0.85)'; gg.font='700 27px Futura, Helvetica, sans-serif'; gg.textAlign='center';
      gg.fillText('NOT LODGED', 0, 10); gg.restore();
    });
    [[27.86,0.55],[28.02,0.32]].forEach((q,i)=>pic(ctx,stamped, q[0], 0.792+i*0.006, q[1], 0, 0.28, 0.14, { rx:-Math.PI/2, rz:i?0.3:-0.15 }));
  }

  /* --- the timeline banner, hung from the roof on two visible cables --- */
  {
    const bn = tex(ctx,1024,384,(gg,w,h)=>{
      gg.fillStyle='#1c1a17'; gg.fillRect(0,0,w,h);
      gg.strokeStyle='#5c5a4e'; gg.lineWidth=2;
      for(let i=0;i<9;i++){ const x=70+i*106; gg.beginPath(); gg.moveTo(x,70); gg.lineTo(x,h-52); gg.stroke();
        gg.fillStyle='#6d675c'; gg.font='16px Menlo, monospace'; gg.textAlign='center';
        gg.fillText(['JUL','AUG','SEP','OCT','NOV','DEC','JAN','FEB','MAR'][i], x, h-28); }
      gg.fillStyle='#cfc8b4'; gg.font='700 32px Futura, Helvetica, sans-serif'; gg.textAlign='left';
      gg.fillText('FY25 — WHAT WAS DONE', 44, 46);
      const bars=[[70,96,530,'evidence gathered','#4a6a5e'],[176,140,420,'costs apportioned','#4a6a5e'],
                  [282,184,318,'narrative written','#4a6a5e'],[388,228,212,'legal reviewed','#4a6a5e'],
                  [600,272,320,'REGISTRATION — never opened','#8a2f2a']];
      bars.forEach(b=>{ gg.fillStyle=b[4]; gg.globalAlpha = b[4]==='#8a2f2a'?0.55:0.85;
        gg.fillRect(b[0],b[1],b[2],26); gg.globalAlpha=1;
        gg.fillStyle='#cfc8b4'; gg.font='17px Futura, Helvetica, sans-serif'; gg.textAlign='left';
        gg.fillText(b[3], b[0]+10, b[1]+19); });
    });
    drop(ctx, 26.20, -11.00, 2.34); drop(ctx, 28.80, -11.00, 2.34);
    ctx.B(2.72,0.05,0.06, p.CHAR, 27.50,2.32,-11.00, 0, world, false);
    ctx.B(2.72,0.05,0.06, p.CHAR, 27.50,1.30,-11.00, 0, world, false);
    pic(ctx,bn, 27.50, 1.81, -11.00, 0, 2.66, 1.00, { both:true, rough:.86 });
  }
}

/* ============================================================================
   PETE GAY — room [17,2 → 21,12.5] · door lane x 18.1..19.9
   Story: a London studio that ate the room — cans, a mural half done, a boombox,
   a board by the door and the photos of everything he's already ruined.
   71 draw calls · bbox x 17.19..20.80, z 3.56..12.31
   NOTE: the host's stood-down block already puts a dust sheet at (19,6.2) and a
   chair at (19,7.3) on top of Pete's easel at (19.15,7.0) — pre-existing, left alone.
   ========================================================================= */
export function dressPete(ctx){
  const T = ctx.THREE, p = pal(ctx), { M, world } = ctx;
  const PAINT = [0xc2543a,0xd98a4a,0xd9c26a,0x8291c9,0x4a6a5e,0xcfc8b4];      // the artTex palette, reused on purpose

  /* --- the crate of cans --- */
  {
    const g = new T.Group(); g.position.set(17.64,0,4.60); g.rotation.y = 0.30; world.add(g);
    ctx.B(0.72,0.34,0.52, M(0x8a7a5c,.92), 0,0.17,0, 0, g);
    ctx.B(0.76,0.04,0.56, M(0x77624a,.92), 0,0.35,0, 0, g, false);
    inst(ctx,new T.BoxGeometry(0.70,0.03,0.03), M(0x77624a,.92), 3,(i,d)=>{ d.position.set(0,0.06+i*0.12,0.265); },false,g);
    inst(ctx,new T.CylinderGeometry(0.031,0.031,0.19,10), M(0xffffff,.45,.35), 15,(i,d)=>{
      d.position.set(-0.26+(i%5)*0.13, 0.44, -0.16+((i/5)|0)*0.16);
      d.rotation.y = i; return PAINT[i%PAINT.length];
    },true,g);
    inst(ctx,new T.CylinderGeometry(0.020,0.020,0.032,10), p.JET, 15,(i,d)=>{
      d.position.set(-0.26+(i%5)*0.13, 0.55, -0.16+((i/5)|0)*0.16);
    },false,g);
    /* the four that got knocked over */
    inst(ctx,new T.CylinderGeometry(0.031,0.031,0.19,10), M(0xffffff,.45,.35), 4,(i,d)=>{
      d.position.set(17.70+Math.sin(i*2.2)*0.22, 0.031, 3.90+Math.cos(i*1.6)*0.24);
      d.rotation.z = Math.PI/2; d.rotation.y = i*1.3; return PAINT[(i+2)%PAINT.length];
    },true);
  }

  /* --- the mural, half painted, on a stretcher standing on its own battens --- */
  {
    const g = new T.Group(); g.position.set(17.36,0,7.20); g.rotation.y = Math.PI/2; world.add(g);
    ctx.B(2.06,0.08,0.06, M(0x8a7a5c,.92), 0,1.90,0, 0, g);
    ctx.B(2.06,0.08,0.06, M(0x8a7a5c,.92), 0,0.10,0, 0, g);
    ctx.B(0.08,1.88,0.06, M(0x8a7a5c,.92), -0.99,1.00,0, 0, g);
    ctx.B(0.08,1.88,0.06, M(0x8a7a5c,.92),  0.99,1.00,0, 0, g);
    ctx.B(0.36,0.05,0.44, M(0x77624a,.92), -0.80,0.025,0.20, 0, g, false);   // the feet
    ctx.B(0.36,0.05,0.44, M(0x77624a,.92),  0.80,0.025,0.20, 0, g, false);
    const brace = ctx.B(0.05,1.30,0.05, M(0x77624a,.92), 0.72,0.68,0.24, 0, g, false); brace.rotation.x = -0.32;
    const mural = tex(ctx,1024,896,(gg,w,h)=>{
      gg.fillStyle='#cfc8b4'; gg.fillRect(0,0,w,h);
      /* the right half is still the pencil grid */
      gg.strokeStyle='rgba(60,56,48,0.30)'; gg.lineWidth=2;
      for(let x=512;x<=w;x+=64){ gg.beginPath(); gg.moveTo(x,0); gg.lineTo(x,h); gg.stroke(); }
      for(let y=0;y<=h;y+=64){ gg.beginPath(); gg.moveTo(512,y); gg.lineTo(w,y); gg.stroke(); }
      gg.strokeStyle='rgba(60,56,48,0.55)'; gg.lineWidth=3;
      gg.beginPath(); gg.moveTo(560,700); gg.bezierCurveTo(650,300, 800,760, 960,240); gg.stroke();
      gg.beginPath(); gg.arc(760,470,150,0,6.3); gg.stroke();
      /* the left half is finished, and loud */
      gg.save(); gg.beginPath(); gg.rect(0,0,516,h); gg.clip();
      gg.fillStyle='#20140f'; gg.fillRect(0,0,516,h);
      const cols = ['#c2543a','#d98a4a','#d9c26a','#8291c9','#4a6a5e','#cfc8b4'];
      let s = 20250830; const rnd=()=>{ s=(s*16807)%2147483647; return (s-1)/2147483646; };
      for(let i=0;i<34;i++){
        gg.strokeStyle = gg.fillStyle = cols[Math.floor(rnd()*cols.length)];
        gg.globalAlpha = 0.55+rnd()*0.45; const k = rnd();
        if(k<0.36){ gg.lineWidth = 12+rnd()*46; gg.beginPath(); gg.moveTo(rnd()*516, rnd()*h);
          gg.bezierCurveTo(rnd()*516,rnd()*h, rnd()*516,rnd()*h, rnd()*516,rnd()*h); gg.stroke(); }
        else if(k<0.64){ gg.fillRect(rnd()*420, rnd()*760, 40+rnd()*160, 34+rnd()*150); }
        else if(k<0.86){ gg.beginPath(); gg.arc(rnd()*516, rnd()*h, 16+rnd()*92, 0, 6.3);
          rnd()<0.5 ? gg.fill() : (gg.lineWidth=6+rnd()*14, gg.stroke()); }
        else { const dx=rnd()*516, dy=rnd()*420; gg.fillRect(dx,dy,6+rnd()*8, 90+rnd()*300);
          gg.beginPath(); gg.arc(dx+5,dy,12+rnd()*12,0,6.3); gg.fill(); }
      }
      gg.globalAlpha=1; gg.restore();
      gg.strokeStyle='rgba(60,56,48,0.5)'; gg.lineWidth=4;
      gg.beginPath(); gg.moveTo(516,0); gg.lineTo(516,h); gg.stroke();
    });
    const mf = new T.Mesh(new T.PlaneGeometry(1.96,1.78), new T.MeshStandardMaterial({ map:mural, roughness:.9 }));
    mf.position.set(0,1.00,0.035); g.add(mf);
  }

  /* --- the boombox, on a crate, permanently on --- */
  {
    const g = new T.Group(); g.position.set(20.45,0,4.30); g.rotation.y = -0.55; world.add(g);
    ctx.B(0.56,0.40,0.44, M(0x8a7a5c,.92), 0,0.20,0, 0, g);
    ctx.B(0.64,0.28,0.24, M(0x2b2b30,.66,.24), 0,0.55,0, 0, g);
    inst(ctx,new T.CylinderGeometry(0.075,0.075,0.030,16), p.JET, 2,(i,d)=>{
      d.position.set(-0.20+i*0.40, 0.55, 0.122); d.rotation.x = Math.PI/2;
    },false,g);
    inst(ctx,new T.CylinderGeometry(0.055,0.055,0.012,14), M(0x4a443a,.8), 2,(i,d)=>{
      d.position.set(-0.20+i*0.40, 0.55, 0.132); d.rotation.x = Math.PI/2;
    },false,g);
    ctx.B(0.16,0.11,0.02, M(0x3a3a41,.5,.3), 0,0.55,0.126, 0, g, false);     // the cassette door
    inst(ctx,new T.CylinderGeometry(0.016,0.016,0.014,10), M(0xd9c26a,.5), 4,(i,d)=>{
      d.position.set(-0.06+i*0.04, 0.44, 0.124); d.rotation.x = Math.PI/2;
    },false,g);
    const vu = tex(ctx,128,32,(gg,w,h)=>{ gg.fillStyle='#0e100e'; gg.fillRect(0,0,w,h);
      for(let i=0;i<14;i++){ gg.fillStyle = i<9?'#8a9a5e':(i<12?'#d9a05e':'#8a2f2a');
        gg.fillRect(4+i*8.6, 8, 6, 16); } });
    const vm = new T.MeshStandardMaterial({ map:vu, emissive:0xffffff, emissiveMap:vu, emissiveIntensity:.7, roughness:.4 });
    ctx.B(0.24,0.05,0.010, vm, 0,0.655,0.126, 0, g, false);
    ctx.SCREENS.push({ m:vm, base:.7 });
    const handle = new T.Mesh(new T.TorusGeometry(0.15,0.014,5,14, Math.PI), p.CHAR);
    handle.position.set(0,0.69,0); handle.rotation.x = Math.PI/2; handle.rotation.z = Math.PI; g.add(handle);
    inst(ctx,new T.BoxGeometry(0.09,0.012,0.065), M(0x3a3a41,.6), 3,(i,d)=>{ // tapes on the crate
      d.position.set(-0.30, 0.406+i*0.014, -0.10+i*0.02); d.rotation.y = i*0.3;
    },false,g);
  }

  /* --- the board, leaning where he stepped off it --- */
  {
    /* outer group yaws, inner group leans, so the tail lands on the floor and stays in the room */
    const go = new T.Group(); go.position.set(20.52,0,5.90); go.rotation.y = -Math.PI/2 + 0.30; world.add(go);
    const g = new T.Group(); g.position.set(0,0.47,0); g.rotation.x = -1.18; go.add(g);
    const deck = tex(ctx,128,512,(gg,w,h)=>{
      gg.fillStyle='#1a1a1d'; gg.fillRect(0,0,w,h);
      gg.fillStyle='#c2543a'; gg.beginPath(); gg.moveTo(0,140); gg.lineTo(w,90); gg.lineTo(w,220); gg.lineTo(0,270); gg.fill();
      gg.fillStyle='#d9c26a'; gg.beginPath(); gg.arc(64,340,44,0,6.3); gg.fill();
      gg.fillStyle='#1a1a1d'; gg.font='700 26px Futura, Helvetica, sans-serif'; gg.textAlign='center';
      gg.save(); gg.translate(64,350); gg.rotate(-0.2); gg.fillText('GAY', 0, 8); gg.restore();
      gg.fillStyle='#4a6a5e'; gg.fillRect(0,420,w,26);
    });
    ctx.B(0.21,0.022,0.78, M(0xd8d2c2,.9), 0,0,0, 0, g);
    const nose = ctx.B(0.19,0.020,0.16, M(0xd8d2c2,.9), 0,0.030,0.44, 0, g, false); nose.rotation.x = -0.42;
    const tail = ctx.B(0.19,0.020,0.16, M(0xd8d2c2,.9), 0,0.030,-0.44, 0, g, false); tail.rotation.x = 0.42;
    const df = new T.Mesh(new T.PlaneGeometry(0.20,0.76), new T.MeshStandardMaterial({ map:deck, roughness:.8 }));
    df.rotation.x = Math.PI/2; df.position.y = -0.013; g.add(df);
    inst(ctx,new T.BoxGeometry(0.13,0.05,0.06), M(0x9a9aa2,.4,.7), 2,(i,d)=>{ d.position.set(0,-0.045,-0.26+i*0.52); },false,g);
    inst(ctx,new T.CylinderGeometry(0.028,0.028,0.024,12), M(0xd9c26a,.5), 4,(i,d)=>{
      d.position.set((i%2?0.085:-0.085), -0.058, (i<2?-0.26:0.26)); d.rotation.z = Math.PI/2;
    },false,g);
  }

  /* --- polaroids on a string, hung off two roof cables --- */
  {
    const sheet = tex(ctx,1024,320,(gg,w,h)=>{
      const cols=['#c2543a','#d98a4a','#d9c26a','#8291c9','#4a6a5e','#cfc8b4'];
      for(let c=0;c<4;c++) for(let r=0;r<2;r++){
        const x=c*256, y=r*160;
        gg.fillStyle='#e4e0d2'; gg.fillRect(x+8,y+6,240,148);
        gg.fillStyle='#1a1a1d'; gg.fillRect(x+18,y+16,220,100);
        let s=(c*7+r*13+3)*9176; const rnd=()=>{ s=(s*16807)%2147483647; return (s-1)/2147483646; };
        for(let i=0;i<7;i++){ gg.fillStyle=cols[Math.floor(rnd()*cols.length)]; gg.globalAlpha=0.5+rnd()*0.5;
          const k=rnd();
          if(k<0.45) gg.fillRect(x+18+rnd()*180, y+16+rnd()*70, 12+rnd()*54, 10+rnd()*46);
          else { gg.beginPath(); gg.arc(x+18+rnd()*210, y+16+rnd()*90, 6+rnd()*26, 0, 6.3); gg.fill(); } }
        gg.globalAlpha=1; gg.fillStyle='#5c5548'; gg.font='italic 17px Georgia, serif'; gg.textAlign='center';
        gg.fillText(['binned it','v2','the good one','too clean','weirder','friday','no','yes?'][c+r*4], x+128, y+140);
      }
    });
    drop(ctx, 17.55, 11.85, 2.22); drop(ctx, 20.45, 11.85, 2.22);
    ctx.B(0.06,0.10,0.06, p.CHAR, 17.55,2.17,11.85, 0, world, false);
    ctx.B(0.06,0.10,0.06, p.CHAR, 20.45,2.17,11.85, 0, world, false);
    [[-0.10,2.140],[0,2.108],[0.10,2.140]].forEach((seg,i)=>{                 // a string that sags
      const c = ctx.Cyl(0.005, 1.02, p.CHAR, 19.00+ (i-1)*0.97, seg[1], 11.85, world, 4);
      c.rotation.z = Math.PI/2 + (i-1)*0.055; c.castShadow = false;
    });
    for(let i=0;i<7;i++){
      const t2 = i/6, x = 17.72 + t2*2.56;
      const sag = 2.128 - Math.sin(t2*Math.PI)*0.030;
      const t3 = sheet.clone(); t3.needsUpdate = true;
      t3.repeat.set(0.25,0.5); t3.offset.set((i%4)*0.25, ((i/4)|0)*0.5);
      const m = new T.Mesh(new T.PlaneGeometry(0.20,0.24),
        new T.MeshStandardMaterial({ map:t3, roughness:.82, side:T.DoubleSide }));
      m.position.set(x, sag-0.15, 11.85); m.rotation.y = Math.PI + (i%3-1)*0.16; m.rotation.z = (i%5-2)*0.045;
      world.add(m);
    }
    inst(ctx,new T.BoxGeometry(0.035,0.026,0.020), M(0x9a9aa2,.4,.6), 7,(i,d)=>{
      const t2 = i/6; d.position.set(17.72+t2*2.56, 2.128-Math.sin(t2*Math.PI)*0.030, 11.85);
    });
  }

  /* --- the uniform, on a hook, with the mask he never wears --- */
  {
    const g = new T.Group(); g.position.set(17.65,0,12.05); g.rotation.y = -0.5; world.add(g);
    ctx.Cyl(0.19,0.04, p.CHAR, 0,0.02,0, g, 14);
    ctx.Cyl(0.028,1.72, p.CHAR, 0,0.88,0, g, 8);
    inst(ctx,new T.BoxGeometry(0.16,0.026,0.026), p.CHAR, 4,(i,d)=>{
      const a = i/4*Math.PI*2; d.position.set(Math.cos(a)*0.075, 1.70, Math.sin(a)*0.075); d.rotation.y = -a;
    },false,g);
    ctx.B(0.42,0.60,0.14, M(0x2f3a34,.96), 0.02,1.34,0.02, 0.1, g);          // the top
    ctx.B(0.13,0.44,0.11, M(0x2f3a34,.96), -0.24,1.36,0.02, 0.3, g);
    ctx.B(0.13,0.44,0.11, M(0x2f3a34,.96),  0.28,1.36,0.02, -0.3, g);
    inst(ctx,new T.BoxGeometry(0.44,0.026,0.15), M(0xd9c26a,.8), 2,(i,d)=>{  // the stripe, matching
      d.position.set(0.02, 1.16+i*0.30, 0.03);
    },false,g);
    ctx.B(0.36,0.56,0.16, M(0x2f3a34,.96), -0.02,0.72,0.02, -0.06, g);       // the bottoms
    inst(ctx,new T.BoxGeometry(0.38,0.024,0.17), M(0xd9c26a,.8), 2,(i,d)=>{ d.position.set(-0.02,0.60+i*0.26,0.03); },false,g);
    /* the respirator, hanging off the same hook */
    ctx.B(0.13,0.10,0.08, M(0x3a3a41,.7), 0.20,1.58,-0.10, 0.4, g, false);
    inst(ctx,new T.CylinderGeometry(0.036,0.036,0.045,12), M(0xd9c26a,.6), 2,(i,d)=>{
      d.position.set(0.20+(i?0.07:-0.07), 1.56, -0.06); d.rotation.x = Math.PI/2; d.rotation.y = 0.4;
    },false,g);
    ctx.Cyl(0.004,0.16, p.CHAR, 0.20,1.66,-0.10, g, 4).castShadow = false;
  }

  /* --- brushes and rags on the desk, and the ladder he leaves out --- */
  {
    ctx.Cyl(0.055,0.14, p.GLASS, 20.35,0.845,11.05, world, 12).castShadow = false;
    inst(ctx,new T.CylinderGeometry(0.006,0.006,0.26,5), M(0x8a7a5c,.9), 7,(i,d)=>{
      const a = i/7*Math.PI*2;
      d.position.set(20.35+Math.cos(a)*0.022, 0.98, 11.05+Math.sin(a)*0.022);
      d.rotation.z = Math.cos(a)*0.22; d.rotation.x = Math.sin(a)*0.22;
      return [0x8a7a5c,0x4a443a,0xc2543a,0xd9c26a,0x4a6a5e,0x8291c9,0xd98a4a][i];
    },true);
    ctx.Cyl(0.048,0.11, M(0x4a443a,.8), 20.50,0.830,10.86, world, 12).castShadow = false;
    b0(ctx,0.19,0.028,0.15, M(0x8f887a,.98), 20.14,0.790,11.16, 0.6);
    b0(ctx,0.17,0.024,0.13, M(0xa8552c,.98), 19.60,0.790,11.10, -0.3);
    /* the ladder */
    const g = new T.Group(); g.position.set(20.30,0,7.30); g.rotation.y = -0.50; world.add(g);
    [[-0.20],[0.20]].forEach(q=>{ const s2 = ctx.Cyl(0.026,1.52, M(0x8a7a5c,.92), q[0],0.76,0.14, g, 6); s2.rotation.x = 0.18; });
    inst(ctx,new T.BoxGeometry(0.44,0.028,0.10), M(0x8a7a5c,.92), 4,(i,d)=>{
      d.position.set(0, 0.24+i*0.36, 0.20 - (0.24+i*0.36)*0.18);
    },false,g);
    [[-0.17],[0.17]].forEach(q=>{ const s3 = ctx.Cyl(0.022,1.48, M(0x77624a,.92), q[0],0.74,-0.20, g, 6); s3.rotation.x = -0.22; });
    ctx.Cyl(0.075,0.15, M(0x8a8578,.5,.4), 0,1.35,0.02, g, 12).castShadow = false;  // a tin on the top step
    ctx.Cyl(0.058,0.014, M(0xc2543a,.6), 0,1.43,0.02, g, 12).castShadow = false;
    /* and the splashes under it */
    [[19.90,8.10,0xd9c26a,0.19],[20.55,8.35,0x8291c9,0.14],[19.55,7.85,0xc2543a,0.11]].forEach(q=>{
      decal(ctx, new T.CircleGeometry(q[3],12),
        new T.MeshStandardMaterial({ color:q[2], roughness:.95, transparent:true, opacity:.42, polygonOffset:true, polygonOffsetFactor:-1 }),
        q[0], q[1], 0.013, q[0]*3);
    });
  }
}

/* ============================================================================
   dressAll — the host calls this once, after the base furnishing.
   Order is west-to-east so anything that reads the world in order sees the
   floor built the way you walk it.
   ========================================================================= */
export function dressAll(ctx){
  dressBen(ctx);
  dressObama(ctx);
  dressShakespeare(ctx);
  dressWozniak(ctx);
  dressIve(ctx);
  dressJobs(ctx);
  dressAssange(ctx);
  dressWrench(ctx);
  dressBuffett(ctx);
  dressJanna(ctx);
  dressLoudfair(ctx);
  dressPete(ctx);
  dressGrants(ctx);
}
